import React, { useEffect, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { Modal } from '../../components/Modal';
import { InvoiceDetailsBody, InvoiceWithDetails as InvoiceDetailFull, LineItem as InvoiceDetailLineItem } from '../../components/InvoiceDetailsBody';
import { formatDateDisplay, formatDateInBusinessTZ, formatDateTimeInBusinessTZ, formatTimeInBusinessTZ, getTodayDateStringInBusinessTZ } from '../../utils/date';
import { trackEvent, subjectMetadata } from '../../utils/analytics';
import { getErrorMessage } from '../../utils/errorMessage';
import { getProgramTypeDisplayName } from '../../types/program';
import { Copy, Edit, Eye, CreditCard, Clock, DollarSign, Ban, AlertTriangle, Mail, Gift } from 'lucide-react';
import FlexSeedButton from '../components/FlexSeedButton';
import BatchStudentPicker from '../components/BatchStudentPicker';
import { isProtectedLineItemConfig } from '../utils/lineItemConfigs';
import { BILLING_POLICY_URL, LATE_FEE, formatLateFeeRate } from '../../config/lateFees';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import { fetchCartHeldInvoiceIds } from '../../hooks/useCartHeldInvoiceIds';
import { isPaymentInFlight } from '../../utils/cart';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { createDateRangeValue, type DateRangeValue } from '../utils/dateRange';
import { AdminBrandTag } from '../components/AdminBrandTag';
import { BrandFilterSelect } from '../components/BrandFilterSelect';
import { BRAND_FILTER_VALUES, brandRpcArg, matchesBrandFilter, type BrandFilter } from '../utils/brandFilter';
import { CartDetailModal, type CartPaymentForRefund } from '../components/finances/CartDetailModal';
import { CartsPanel } from '../components/finances/CartsPanel';
import { FocusedRecordBanner } from '../components/FocusedRecordBanner';
import { INVOICE_PARAM, PAYMENT_PARAM, useFocusedRecordId } from '../utils/recordLinks';

type FinancesTab = 'invoices' | 'payments' | 'carts' | 'refunds' | 'debt';
const FINANCES_TABS: readonly FinancesTab[] = ['invoices', 'payments', 'carts', 'refunds', 'debt'];

// Custom CSS for peaceful animations
const peacefulAnimations = `
  @keyframes gentleFloat {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-2px); }
  }
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

// Inject the styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = peacefulAnimations;
  if (!document.head.querySelector('style[data-peaceful-animations]')) {
    styleSheet.setAttribute('data-peaceful-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

interface PaymentData {
  id: string;
  amount: number;
  student_name: string;
  program_name: string;
  period_start?: string | null;
  period_end?: string | null;
  payment_method: string;
  status: string;
  created_at: string;
  refunded_amount?: number;
  paid_by_parent_id?: string | null;
  paid_by_parent_name?: string;
  paid_by_parent_email?: string | null;
  /** `programs.brand_key` of the invoice's enrollment. */
  brand_key?: string;
  /** Set when this payment is one invoice's share of a bundle payment (payment_carts). */
  cart_id?: string | null;
}

interface InvoiceData {
  id: string;
  student_name: string;
  program_name: string;
  total_amount: number;
  amount_paid: number;
  status: string;
  due_date: string;
  period_start: string;
  period_end: string;
  created_at: string;
  updated_at: string;
  memo_code: string | null;
  enrolled_by_parent_name?: string;
  enrolled_by_parent_email?: string | null;
  email_suppressed?: boolean;
  /** `programs.brand_key`; passed to send-email so the mail names the brand that sold the class. */
  brand_key?: string;
}

/**
 * Names to log with an invoice event, so the row still reads after the invoice is gone.
 * See `subjectMetadata` — an unenroll cascade hard-deletes invoices, and a read-time lookup
 * has nothing left to resolve.
 */
const invoiceSubject = (invoice: Pick<InvoiceData, 'student_name' | 'program_name'>) =>
  subjectMetadata({ studentName: invoice.student_name, programName: invoice.program_name });

interface RefundModalData {
  isOpen: boolean;
  paymentId: string | null;
  paymentAmount: number;
  studentName: string;
  programName: string;
  amount: number;
  isProcessing: boolean;
  error: string | null;
  paymentMethod: string;
  refundMethod: string;
  refundNotes: string;
  refunded_amount?: number;
  parentId: string | null;
  bucket: 'withdrawable' | 'non_withdrawable';
  /** The bundle payment this is one invoice's share of. */
  cartId?: string | null;
}

interface ManualPaymentModalData {
  isOpen: boolean;
  invoiceId: string | null;
  studentName: string;
  programName: string;
  totalAmount: number;
  amountPaid: number;
  remainingAmount: number;
  paymentAmount: number;
  paymentMethod: string;
  paymentDate: string;
  isProcessing: boolean;
  error: string | null;
}

interface InvoiceLineItemData {
  id: string;
  description: string | null;
  amount: number;
  line_item_configs: {
    id: string;
    name: string;
    type: string;
  } | null;
  is_optional?: boolean;
}

interface InvoiceDetailsModalData {
  isOpen: boolean;
  invoice: InvoiceData | null;
  /** Full invoice shape (enrollment → program, payments) for the shared detail body. */
  detail: InvoiceDetailFull | null;
  lineItems: InvoiceDetailLineItem[];
  isLoading: boolean;
  error: string | null;
}

interface EnrollmentDetails {
  id: string;
  enrollment_start_date: string;
  selected_days: string[] | null;
  opt_in_items: string[] | null;
  program: {
    id: string;
    name: string;
    type: string;
    billing_type: string;
    price: number;
  };
  student: {
    first_name: string;
    last_name: string;
  };
  optInItemsDetails?: Array<{
    id: string;
    name: string;
    amount: number;
    type: string;
  }>;
  availableOptionalItems?: Array<{
    id: string;
    name: string;
    amount: number;
    type: string;
    description: string;
  }>;
}

interface EnrollmentQueryResponse {
  enrollment_id: string;
  enrollments: {
    id: string;
    enrollment_start_date: string;
    selected_days: string[] | null;
    opt_in_items: string[] | null;
    programs: {
      id: string;
      name: string;
      type: string;
      billing_type: string;
      price: number;
    };
    students: {
      first_name: string;
      last_name: string;
    };
  };
}

interface PricingQueryItem {
  amount: number;
  line_item_configs: {
    id: string;
    name: string;
    type: string;
    description?: string;
  }[];
}

interface OptionalItemQueryItem {
  amount: number;
  is_optional: boolean;
  line_item_configs: {
    id: string;
    name: string;
    type: string;
    description?: string;
  }[];
}

interface EditInvoiceModalData {
  isOpen: boolean;
  invoice: InvoiceData | null;
  lineItems: InvoiceLineItemData[];
  enrollmentDetails: EnrollmentDetails | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  editedStatus: string;
  editedLineItems: Array<{
    id: string;
    description: string;
    amount: number;
    isEdited: boolean;
    // Set for newly-added items so the save path can resolve the config
    // directly instead of matching by description.
    lineItemConfigId?: string;
  }>;
  // Full catalog of line item configs, used for the "add custom line item"
  // flow available on email-suppressed invoices.
  allLineItemConfigs?: Array<{
    id: string;
    name: string;
    type: string;
    subtype: string | null;
    description: string | null;
  }>;
}

interface RefundData {
  id: string;
  payment_id: string;
  amount: number;
  student_name: string;
  program_name: string;
  period_start?: string | null;
  period_end?: string | null;
  refund_method: string;
  status: string;
  refund_date: string;
  created_at: string;
  original_payment_amount: number;
  original_payment_method: string;
  refund_notes?: string | null;
  paid_by_parent_id?: string | null;
  paid_by_parent_name?: string;
  paid_by_parent_email?: string | null;
  /** `programs.brand_key` of the refunded payment's enrollment. */
  brand_key?: string;
}

// Helper function to copy payment ID to clipboard
const copyPaymentId = (paymentId: string) => {
  navigator.clipboard.writeText(paymentId);
  alert('Payment ID copied to clipboard!');
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** '2026-05' → 'May 2026'. Built from the string parts so no timezone is involved. */
function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-');
  const name = MONTH_NAMES[Number(month) - 1];
  return name ? `${name} ${year}` : yearMonth;
}

// Helper function to copy invoice ID to clipboard
const copyInvoiceId = (invoiceId: string) => {
  navigator.clipboard.writeText(invoiceId);
  alert('Invoice ID copied to clipboard!');
};

// Helper function to check if invoice is more than 1 day past due
const isInvoiceLate = (dueDate: string): boolean => {
  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 1;
};


export function AdminFinances() {
  // Period fields are stored as DATE-like (or TIMESTAMPTZ set from a YYYY-MM-DD).
  // Display the exact dates passed in (no TZ shifting), per project rules.
  function formatPeriodRange(periodStart?: string | null, periodEnd?: string | null): string {
    if (!periodStart || !periodEnd) return '—'
    return `${formatDateDisplay(periodStart)} - ${formatDateDisplay(periodEnd)}`
  }

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);

  const [activeTab, setActiveTab] = useUrlTabState<FinancesTab>('tab', FINANCES_TABS, 'invoices');
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [debtLoading, setDebtLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentsData, setPaymentsData] = useState<PaymentData[]>([]);
  const [invoicesData, setInvoicesData] = useState<InvoiceData[]>([]);
  const [refundsData, setRefundsData] = useState<RefundData[]>([]);
const [invoicesWithLateFees, setInvoicesWithLateFees] = useState<Set<string>>(new Set());
  const [invoicesWithSecondLateFees, setInvoicesWithSecondLateFees] = useState<Set<string>>(new Set());
  const [processingLateFees, setProcessingLateFees] = useState<Set<string>>(new Set());
  const [lateFeeActions, setLateFeeActions] = useState<Map<string, 'adding' | 'escalating' | 'waiving'>>(new Map());

  // Pagination
  const PAGE_SIZE = 50;
  const [paymentsPage, setPaymentsPage] = useState<number>(1);
  const [paymentsTotal, setPaymentsTotal] = useState<number>(0);
  const [invoicesPage, setInvoicesPage] = useState<number>(1);
  const [invoicesTotal, setInvoicesTotal] = useState<number>(0);

  // Debt tab state (configurable days overdue invoices)
  const [debtData, setDebtData] = useState<InvoiceData[]>([]);
  const [debtPage, setDebtPage] = useState<number>(1);
  const [debtTotal, setDebtTotal] = useState<number>(0);
  const [debtSort, setDebtSort] = useState<{
    column: 'student_name' | 'program_name' | 'amount' | 'due_date' | 'created_at';
    direction: 'asc' | 'desc';
  }>({ column: 'due_date', direction: 'asc' });
  const [debtDaysThreshold, setDebtDaysThreshold] = useState<number>(30);
  const [debtDaysDisplay, setDebtDaysDisplay] = useState<number>(30); // For immediate slider feedback

  // One brand scope for all four tabs, in the URL so a filtered view can be linked. A payment,
  // invoice or refund takes its enrollment's program's brand. Changing it starts each paged
  // list over: page 4 of every brand is not page 4 of one.
  const [brandFilter, setBrandFilterParam] = useUrlTabState<BrandFilter>('brand', BRAND_FILTER_VALUES, 'all');
  // `?invoice=<id>` / `?payment=<id>` (from search, the Overview, or a pasted link) narrow
  // the Invoices / Payments tab to that one row. Any search or filter change means "show me
  // the list again", so each one lets go of it.
  const [focusedInvoiceId, clearFocusedInvoice] = useFocusedRecordId(INVOICE_PARAM);
  const [focusedPaymentId, clearFocusedPayment] = useFocusedRecordId(PAYMENT_PARAM);
  const releaseFocusedInvoice = () => {
    if (focusedInvoiceId) clearFocusedInvoice();
  };
  const releaseFocusedPayment = () => {
    if (focusedPaymentId) clearFocusedPayment();
  };
  const setBrandFilter = (next: BrandFilter) => {
    setPaymentsPage(1);
    setInvoicesPage(1);
    setDebtPage(1);
    // The brand and the links all live in the URL, so they change in one write ('all' is
    // the default brand and leaves the param off, as useUrlTabState does).
    if (focusedInvoiceId || focusedPaymentId) {
      clearFocusedInvoice(params => {
        params.delete(PAYMENT_PARAM);
        if (next === 'all') params.delete('brand');
        else params.set('brand', next);
      });
    } else {
      setBrandFilterParam(next);
    }
  };
  const [voidingInvoice, setVoidingInvoice] = useState<string | null>(null);
  const [compingInvoice, setCompingInvoice] = useState<string | null>(null);
  const [sendingInvoiceEmail, setSendingInvoiceEmail] = useState<string | null>(null); // Invoice ID having its creation email sent
  const [sendingReminder, setSendingReminder] = useState<string | null>(null); // Invoice ID being sent a reminder
  const [sendingBulkReminders, setSendingBulkReminders] = useState(false);
  const [sendingLateFeeNotice, setSendingLateFeeNotice] = useState<string | null>(null); // Invoice ID being sent a late fee notice
  const [reminderToast, setReminderToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  // The bundle payment open in CartDetailModal.
  const [openCartId, setOpenCartId] = useState<string | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  // Arriving on a link opens that invoice's row, and starts the pager over: a link followed
  // from page 3 of the full list should not read "101–1 of 1".
  useEffect(() => {
    if (!focusedInvoiceId) return;
    setSelectedInvoiceId(focusedInvoiceId);
    setInvoicesPage(1);
  }, [focusedInvoiceId]);
  useEffect(() => {
    if (!focusedPaymentId) return;
    setSelectedPaymentId(focusedPaymentId);
    setPaymentsPage(1);
  }, [focusedPaymentId]);
  const [selectedRefundId, setSelectedRefundId] = useState<string | null>(null);
  const [selectedDebtId, setSelectedDebtId] = useState<string | null>(null);

  const [paymentFilters, setPaymentFilters] = useState<{ search: string; status: string } & DateRangeValue>(
    () => ({ search: '', ...createDateRangeValue(), status: 'all' }),
  );
  const [appliedPaymentSearch, setAppliedPaymentSearch] = useState('');

  const [invoiceFilters, setInvoiceFilters] = useState<{ search: string; status: string } & DateRangeValue>(
    () => ({ search: '', ...createDateRangeValue(), status: 'all' }),
  );

  const [refundFilters, setRefundFilters] = useState<{ search: string; status: string } & DateRangeValue>(
    () => ({ search: '', ...createDateRangeValue(), status: 'all' }),
  );

  const [paymentSort, setPaymentSort] = useState<{
    column: 'student_name' | 'program_name' | 'amount' | 'created_at';
    direction: 'asc' | 'desc';
  }>({ column: 'created_at', direction: 'desc' });

  const [invoiceSort, setInvoiceSort] = useState<{
    column: 'student_name' | 'program_name' | 'amount' | 'created_at' | 'updated_at';
    direction: 'asc' | 'desc';
  }>({ column: 'created_at', direction: 'desc' });
  const [appliedInvoiceSearch, setAppliedInvoiceSearch] = useState('');

  const [refundSort, setRefundSort] = useState<{
    column: 'student_name' | 'program_name' | 'amount' | 'refund_date' | 'created_at';
    direction: 'asc' | 'desc';
  }>({ column: 'created_at', direction: 'desc' });

  const [refundModal, setRefundModal] = useState<RefundModalData>({
    isOpen: false,
    paymentId: null,
    paymentAmount: 0,
    studentName: '',
    programName: '',
    amount: 0,
    isProcessing: false,
    error: null,
    paymentMethod: '',
    refundMethod: '',
    refundNotes: '',
    refunded_amount: 0,
    parentId: null,
    bucket: 'non_withdrawable',
  });

  const [manualPaymentModal, setManualPaymentModal] = useState<ManualPaymentModalData>({
    isOpen: false,
    invoiceId: null,
    studentName: '',
    programName: '',
    totalAmount: 0,
    amountPaid: 0,
    remainingAmount: 0,
    paymentAmount: 0,
    paymentMethod: 'check',
    paymentDate: getTodayDateStringInBusinessTZ(),
    isProcessing: false,
    error: null
  });

  const [invoiceDetailsModal, setInvoiceDetailsModal] = useState<InvoiceDetailsModalData>({
    isOpen: false,
    invoice: null,
    detail: null,
    lineItems: [],
    isLoading: false,
    error: null,
  });

  const [editInvoiceModal, setEditInvoiceModal] = useState<EditInvoiceModalData>({
    isOpen: false,
    invoice: null,
    lineItems: [],
    enrollmentDetails: null,
    isLoading: false,
    isSaving: false,
    error: null,
    editedStatus: '',
    editedLineItems: [],
  });

  // When an invoice has its email suppressed, it has not yet been communicated
  // to the parent, so admins are allowed to fully restructure the line items:
  // delete any line item (not just optional ones) and append a custom line item
  // built from any line item config.
  const canFreelyEditInvoice = Boolean(editInvoiceModal.invoice?.email_suppressed);
  // Currently-selected config in the "add custom line item" picker.
  const [customLineItemConfigId, setCustomLineItemConfigId] = useState<string>('');

  // Config ids already present on the invoice across current edits. Used to
  // prevent the same line item config from being applied more than once.
  const presentLineItemConfigIds = new Set(
    editInvoiceModal.editedLineItems
      .map(ei => {
        if (ei.id.startsWith('temp_')) return ei.lineItemConfigId;
        const original = editInvoiceModal.lineItems.find(li => li.id === ei.id);
        return original?.line_item_configs?.id;
      })
      .filter((id): id is string => Boolean(id))
  );

  // ==== Recurring Monthly Invoice Batch Modal State ====
  interface BatchRunResultSummary {
    timestamp: string;
    created: number;
    skipped: number;
    message?: string;
  }

  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchMonth, setBatchMonth] = useState<string>(() => {
    const now = new Date();
    // Default to next month in business TZ-like semantics (no TZ dependency here)
    const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const year = nextMonthDate.getFullYear();
    const month = String(nextMonthDate.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`; // YYYY-MM for next month
  });
  const [batchLimit, setBatchLimit] = useState<string>('');
  // When set, the run is scoped to this student and the count limit does not apply.
  const [batchStudentId, setBatchStudentId] = useState<string>('');
  const [batchProrationOverride, setBatchProrationOverride] = useState<string>('');
  const [batchSuppressEmail, setBatchSuppressEmail] = useState<boolean>(false);
  const [batchRunResults, setBatchRunResults] = useState<BatchRunResultSummary[]>([]);

  // Mutation to run the edge function
  type BatchResultItem = {
    enrollment_id: string;
    success: boolean;
    skipped?: boolean;
    message?: string;
    invoice_id?: string;
    error?: string;
  };

  // Preview state (admin-first prioritized order) when month and limit are set
  interface RecurringInvoicePreviewItem {
    enrollment_id: string;
    amount: number;
    email_to: string | null;
    /** False for rows shown for visibility that create no invoice (e.g. flex pause). */
    would_create?: boolean;
    line_items?: Array<{ name: string; amount: number; proration_factor?: number }>;
  }
  interface RecurringInvoicePreviewResponse {
    success?: boolean;
    preview?: RecurringInvoicePreviewItem[];
    total_candidates?: number;
    /** Sent back as expected_plan_hash so the run writes exactly what was previewed. */
    plan_hash?: string;
    error?: string;
  }
  const [previewItems, setPreviewItems] = useState<RecurringInvoicePreviewItem[]>([]);
  const [previewPlanHash, setPreviewPlanHash] = useState<string | null>(null);
  // Bumped after a plan_changed refusal so the preview reloads with what a run would do now.
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [previewTotalCandidates, setPreviewTotalCandidates] = useState<number | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  interface RecurringInvoiceResponse {
    success?: boolean;
    results?: BatchResultItem[];
    message?: string;
    created?: number;
    skipped?: number;
    error?: string;
    summary?: {
      total_enrollments: number;
      invoices_created: number;
      limit_applied: number | 'none';
      skipped_due_to_limit: number;
      student_id_filter?: string | null;
    }
  }

  const [batchRunDetails, setBatchRunDetails] = useState<BatchResultItem[][]>([]);

  // ==== Autopay Batch Modal State ====
  interface AutopayPreviewItem {
    invoice_id: string;
    enrollment_id: string;
    status: string;
    due_date: string;
    amount_due: number;
    /** Taken from BHCS Cash first; amount_to_charge is what is left for card/ACH, plus fee. */
    bhcs_cash_applied?: number;
    card_fee: number;
    amount_to_charge: number;
    payment_method?: 'card' | 'ach';
    parent_email?: string;
    stripe_customer_action?: 'existing' | 'created' | 'would_create';
    existing_payment?: boolean;
    would_charge: boolean;
    skipped?: boolean;
    reason?: string;
  }
  interface AutopayPreviewResponse {
    success?: boolean;
    preview?: boolean;
    results?: AutopayPreviewItem[];
    /** Sent back as expected_plan_hash so the run charges exactly what was previewed. */
    plan_hash?: string;
    error?: string;
    summary?: {
      invoices_total: number;
      would_charge: number;
      skipped: number;
      limit_applied: number | 'none';
      student_id_filter?: string | null;
      period_start?: string | null;
    };
  }

  interface AutopayRunResultItem {
    invoice_id: string;
    enrollment_id?: string;
    success?: boolean;
    skipped?: boolean;
    reason?: string;
    error?: string;
    payment_intent_id?: string;
    status?: string;
    parent_email?: string;
    payment_method?: string;
    amount_to_charge?: number;
  }
  interface AutopayRunResponse {
    success?: boolean;
    preview?: boolean;
    results?: AutopayRunResultItem[];
    error?: string;
    summary?: {
      invoices_total: number;
      success: number;
      skipped: number;
      failures: number;
      charged?: number;
      limit_applied: number | 'none';
      student_id_filter?: string | null;
      period_start?: string | null;
    };
  }
  interface AutopayRunResultSummary {
    timestamp: string;
    charged: number;
    skipped: number;
    failures: number;
    message?: string;
  }

  const [showAutopayModal, setShowAutopayModal] = useState(false);
  const [autopayLimit, setAutopayLimit] = useState<string>('');
  // Optional scoping: one student, one billed month, or both. Empty means "everything outstanding".
  const [autopayStudentId, setAutopayStudentId] = useState<string>('');
  const [autopayMonth, setAutopayMonth] = useState<string>('');
  // Months with an outstanding recurring_monthly invoice — the only months autopay can act on.
  const [autopayMonthOptions, setAutopayMonthOptions] = useState<string[]>([]);
  const [autopayMonthsLoaded, setAutopayMonthsLoaded] = useState(false);
  const [autopayPreviewItems, setAutopayPreviewItems] = useState<AutopayPreviewItem[]>([]);
  const [isLoadingAutopayPreview, setIsLoadingAutopayPreview] = useState(false);
  const [autopayPreviewError, setAutopayPreviewError] = useState<string | null>(null);
  const [autopayPlanHash, setAutopayPlanHash] = useState<string | null>(null);
  // Bumped after a plan_changed refusal so the preview reloads with what a run would do now.
  const [autopayPreviewRefreshKey, setAutopayPreviewRefreshKey] = useState(0);
  const [autopayRunResults, setAutopayRunResults] = useState<AutopayRunResultSummary[]>([]);
  const [autopayRunDetails, setAutopayRunDetails] = useState<AutopayRunResultItem[][]>([]);
  const [expandedAutopayRuns, setExpandedAutopayRuns] = useState<{ index: number; open: boolean; details: AutopayRunResultItem[] }[]>([]);

  const runAutopayMutation = useMutation({
    mutationFn: async (payload: { limit?: number; student_id?: string; period_start?: string; expected_plan_hash?: string }) => {
      const { data, error } = await supabase.functions.invoke('autopay', {
        method: 'POST',
        body: payload,
      });
      if (error) {
        const status = (error as { context?: { status?: number } }).context?.status;
        if (status === 409) {
          setAutopayPreviewRefreshKey((k) => k + 1);
          throw new Error('The charges changed since this preview, so nobody was charged. Review the refreshed preview and run again.');
        }
        throw new Error(error.message);
      }
      return data as AutopayRunResponse;
    },
    onSuccess: (data) => {
      const details = Array.isArray(data.results) ? data.results : [];
      const charged = details.filter((r) => Boolean(r.success)).length;
      const skipped = details.filter((r) => Boolean(r.skipped)).length;
      const failures = details.filter((r) => !r.success && !r.skipped).length;
      const summary: AutopayRunResultSummary = {
        timestamp: new Date().toISOString(),
        charged,
        skipped,
        failures,
        message: data.summary ? `processed ${data.summary.invoices_total}, limit ${data.summary.limit_applied}` : undefined,
      };
      setAutopayRunResults((prev) => [summary, ...prev].slice(0, 10));
      setAutopayRunDetails((prev) => [details, ...prev].slice(0, 10));
      // Prefetch enrollment names for display (best-effort)
      const fauxDetails: BatchResultItem[] = details
        .filter((d): d is AutopayRunResultItem & { enrollment_id: string } => typeof d.enrollment_id === 'string' && d.enrollment_id.length > 0)
        .map((d) => ({ enrollment_id: d.enrollment_id, success: true, invoice_id: d.invoice_id }));
      if (fauxDetails.length > 0) augmentBatchRunDetails(fauxDetails).catch(() => {});
      setExpandedAutopayRuns((prev) => [{ index: 0, open: true, details }, ...prev.map((er) => ({ ...er, index: er.index + 1 }))]);

      // Refresh visible data
      fetchPayments();
      fetchInvoices();
    },
    onError: (err: unknown) => {
      const summary: AutopayRunResultSummary = {
        timestamp: new Date().toISOString(),
        charged: 0,
        skipped: 0,
        failures: 0,
        message: getErrorMessage(err),
      };
      setAutopayRunResults((prev) => [summary, ...prev].slice(0, 10));
    },
  });

  function runAutopay() {
    // A student-scoped run ignores the limit, matching `bhcs batch run-autopay --student`.
    const limitNum = autopayStudentId ? undefined : (autopayLimit ? Number(autopayLimit) : undefined);
    if (limitNum !== undefined && (!Number.isInteger(limitNum) || limitNum < 1)) {
      const summary: AutopayRunResultSummary = {
        timestamp: new Date().toISOString(),
        charged: 0,
        skipped: 0,
        failures: 0,
        message: 'Limit must be a positive integer',
      };
      setAutopayRunResults((prev) => [summary, ...prev].slice(0, 10));
      return;
    }
    runAutopayMutation.mutate({
      ...(limitNum ? { limit: limitNum } : {}),
      ...(autopayStudentId ? { student_id: autopayStudentId } : {}),
      ...(autopayMonth ? { period_start: `${autopayMonth}-01` } : {}),
      // Present only when a preview for exactly these inputs has loaded.
      ...(autopayPlanHash ? { expected_plan_hash: autopayPlanHash } : {}),
    });
  }

  const runMonthlyInvoicesMutation = useMutation({
    mutationFn: async (payload: { period_start: string; limit?: number; student_id?: string; proration_override?: number; suppress_email?: boolean; expected_plan_hash?: string }) => {
      const { data, error } = await supabase.functions.invoke('create-recurring-monthly-invoice', {
        method: 'POST',
        body: payload,
      });
      if (error) {
        const status = (error as { context?: { status?: number } }).context?.status;
        if (status === 409) {
          setPreviewRefreshKey((k) => k + 1);
          throw new Error('The invoices changed since this preview, so nothing was created. Review the refreshed preview and run again.');
        }
        throw new Error(error.message);
      }
      return data as RecurringInvoiceResponse;
    },
    onSuccess: (data) => {
      const details = Array.isArray(data.results) ? data.results : [];
      const createdFromDetails = details.filter(r => r.success && !r.skipped).length;
      const skippedFromDetails = details.filter(r => r.skipped).length;
      const created = details.length > 0
        ? createdFromDetails
        : (typeof data.summary?.invoices_created === 'number'
            ? data.summary!.invoices_created
            : (typeof data.created === 'number' ? data.created : 0));
      const skipped = details.length > 0
        ? skippedFromDetails
        : (typeof data.summary?.skipped_due_to_limit === 'number'
            ? data.summary!.skipped_due_to_limit
            : (typeof data.skipped === 'number' ? data.skipped : 0));
      const summary: BatchRunResultSummary = {
        timestamp: new Date().toISOString(),
        created,
        skipped,
        message: data.summary ? `processed ${data.summary.total_enrollments}, limit ${data.summary.limit_applied}` : data.message,
      };
      setBatchRunResults(prev => [summary, ...prev].slice(0, 10));
      setBatchRunDetails(prev => [details, ...prev].slice(0, 10));
      // Prefetch info for better display
      augmentBatchRunDetails(details).catch(() => {});
      // Reindex expanded rows to keep them aligned after unshifting
      setExpandedRuns(prev => [{ index: 0, open: true, details }, ...prev.map(er => ({ ...er, index: er.index + 1 }))]);
      // Refresh invoices after running
      fetchInvoices();
    },
    onError: (err: unknown) => {
      const summary: BatchRunResultSummary = {
        timestamp: new Date().toISOString(),
        created: 0,
        skipped: 0,
        message: getErrorMessage(err),
      };
      setBatchRunResults(prev => [summary, ...prev].slice(0, 10));
    },
  });

  function runMonthlyInvoices() {
    if (!batchMonth) return;
    const periodStart = `${batchMonth}-01`;
    // A student-scoped run ignores the limit entirely (same as `--student` in the CLI).
    const limitNum = batchStudentId ? undefined : (batchLimit ? Number(batchLimit) : undefined);
    if (limitNum !== undefined && (!Number.isInteger(limitNum) || limitNum < 1)) {
      const summary: BatchRunResultSummary = {
        timestamp: new Date().toISOString(),
        created: 0,
        skipped: 0,
        message: 'Limit must be a positive integer',
      };
      setBatchRunResults(prev => [summary, ...prev].slice(0, 10));
      return;
    }
    const overrideNum = batchProrationOverride !== '' ? Number(batchProrationOverride) : undefined;
    if (overrideNum !== undefined) {
      const finite = Number.isFinite(overrideNum);
      const inRange = finite && overrideNum >= 0 && overrideNum <= 1;
      if (!inRange) {
        const summary: BatchRunResultSummary = {
          timestamp: new Date().toISOString(),
          created: 0,
          skipped: 0,
          message: 'Proration override must be a number between 0 and 1',
        };
        setBatchRunResults(prev => [summary, ...prev].slice(0, 10));
        return;
      }
    }
    runMonthlyInvoicesMutation.mutate({
      period_start: periodStart,
      ...(limitNum ? { limit: limitNum } : {}),
      ...(batchStudentId ? { student_id: batchStudentId } : {}),
      ...(overrideNum !== undefined ? { proration_override: overrideNum } : {}),
      ...(batchSuppressEmail ? { suppress_email: true } : {}),
      // Present only when a preview for exactly these inputs has loaded.
      ...(previewPlanHash ? { expected_plan_hash: previewPlanHash } : {}),
    });
  }

  // Expandable results state
  const [expandedRuns, setExpandedRuns] = useState<{ index: number; open: boolean; details: BatchResultItem[] }[]>([]);
  const [enrollmentInfoCache, setEnrollmentInfoCache] = useState<Map<string, { studentName: string; programName: string }>>(new Map());
  const [invoiceAmountCache, setInvoiceAmountCache] = useState<Map<string, number>>(new Map());

  // Fetch preview whenever modal open, month set, and a valid limit is present
  useEffect(() => {
    // Any input change invalidates the approved plan until the new preview arrives.
    setPreviewPlanHash(null);
    if (!showBatchModal) return;
    if (!batchMonth) return;
    // A student-scoped preview needs no limit; an unscoped one still requires one so we
    // never preview the entire school by accident.
    if (!batchStudentId && !batchLimit) {
      setPreviewItems([]);
      setPreviewTotalCandidates(null);
      setPreviewError(null);
      return;
    }
    const limitNum = batchStudentId ? undefined : Number(batchLimit);
    if (limitNum !== undefined && (!Number.isInteger(limitNum) || limitNum < 1)) {
      setPreviewItems([]);
      setPreviewTotalCandidates(null);
      setPreviewError('Limit must be a positive integer');
      return;
    }
    let cancelled = false;
    const fetchPreview = async () => {
      try {
        setIsLoadingPreview(true);
        setPreviewError(null);
        const periodStart = `${batchMonth}-01`;
        // Validate optional proration override
        let overrideNum: number | undefined = undefined;
        if (batchProrationOverride !== '') {
          const parsed = Number(batchProrationOverride);
          const finite = Number.isFinite(parsed);
          const inRange = finite && parsed >= 0 && parsed <= 1;
          if (!inRange) {
            setPreviewItems([]);
            setPreviewTotalCandidates(null);
            setPreviewError('Proration override must be a number between 0 and 1');
            setIsLoadingPreview(false);
            return;
          }
          overrideNum = parsed;
        }
        const { data, error } = await supabase.functions.invoke('create-recurring-monthly-invoice', {
          method: 'POST',
          body: {
            period_start: periodStart,
            preview: true,
            ...(limitNum !== undefined ? { limit: limitNum } : {}),
            ...(batchStudentId ? { student_id: batchStudentId } : {}),
            ...(overrideNum !== undefined ? { proration_override: overrideNum } : {}),
          },
        });
        if (cancelled) return;
        if (error) throw new Error(error.message);
        const resp = data as RecurringInvoicePreviewResponse;
        const items = Array.isArray(resp.preview) ? resp.preview : [];
        setPreviewItems(items);
        setPreviewTotalCandidates(typeof resp.total_candidates === 'number' ? resp.total_candidates : null);
        setPreviewPlanHash(typeof resp.plan_hash === 'string' ? resp.plan_hash : null);
        // Augment names for display
        const fauxDetails: BatchResultItem[] = items.map((p) => ({ enrollment_id: p.enrollment_id, success: true }));
        if (fauxDetails.length > 0) {
          await augmentBatchRunDetails(fauxDetails);
        }
      } catch (e) {
        if (cancelled) return;
        setPreviewItems([]);
        setPreviewTotalCandidates(null);
        setPreviewError(e instanceof Error ? e.message : 'Failed to load preview');
      } finally {
        if (!cancelled) setIsLoadingPreview(false);
      }
    };
    fetchPreview();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBatchModal, batchMonth, batchLimit, batchStudentId, batchProrationOverride, previewRefreshKey]);

  // Load the months the month filter can offer. Autopay only ever charges
  // recurring_monthly invoices, so the list has to be narrowed the same way the function
  // narrows — listing every month with any outstanding invoice would offer months whose
  // debt is all fixed_term or per_day, which can never produce a single charge.
  useEffect(() => {
    if (!showAutopayModal) return;
    let cancelled = false;
    setAutopayMonthsLoaded(false);
    const loadMonths = async () => {
      try {
        const { data: invoiceRows, error: invoiceError } = await supabase
          .from('invoices')
          .select('period_start, enrollment_id')
          .in('status', ['pending', 'partial', 'overdue'])
          .not('period_start', 'is', null)
          .order('period_start', { ascending: false });
        if (cancelled || invoiceError) return;
        const rows = (invoiceRows ?? []) as Array<{ period_start: string | null; enrollment_id: string | null }>;
        const enrollmentIds = Array.from(
          new Set(rows.map((r) => r.enrollment_id).filter((v): v is string => Boolean(v))),
        );
        if (enrollmentIds.length === 0) {
          setAutopayMonthOptions([]);
          setAutopayMonthsLoaded(true);
          return;
        }

        const { data: enrollmentRows } = await supabase
          .from('enrollments')
          .select('id, program_id')
          .in('id', enrollmentIds);
        const enrollments = (enrollmentRows ?? []) as Array<{ id: string; program_id: string }>;
        const programIds = Array.from(new Set(enrollments.map((e) => e.program_id)));
        const { data: programRows } = await supabase
          .from('programs')
          .select('id, billing_type')
          .in('id', programIds);
        if (cancelled) return;
        const recurringProgramIds = new Set(
          ((programRows ?? []) as Array<{ id: string; billing_type: string }>)
            .filter((pr) => pr.billing_type === 'recurring_monthly')
            .map((pr) => pr.id),
        );
        const autopayableEnrollmentIds = new Set(
          enrollments.filter((e) => recurringProgramIds.has(e.program_id)).map((e) => e.id),
        );

        const months = Array.from(
          new Set(
            rows
              .filter((r) => r.enrollment_id && autopayableEnrollmentIds.has(r.enrollment_id))
              .map((r) => r.period_start)
              .filter((v): v is string => Boolean(v))
              .map((v) => v.slice(0, 7)),
          ),
        );
        setAutopayMonthOptions(months);
        setAutopayMonthsLoaded(true);
      } catch {
        // Non-fatal: the filter still offers "All outstanding months".
      }
    };
    loadMonths();
    return () => {
      cancelled = true;
    };
  }, [showAutopayModal]);

  // Fetch autopay preview whenever the modal is open and the run is bounded — by a
  // limit, a student, or a month. Unbounded stays un-previewed on purpose.
  useEffect(() => {
    // Any input change invalidates the approved plan until the new preview arrives.
    setAutopayPlanHash(null);
    if (!showAutopayModal) return;
    if (!autopayLimit && !autopayStudentId && !autopayMonth) {
      setAutopayPreviewItems([]);
      setAutopayPreviewError(null);
      return;
    }
    const limitNum = autopayStudentId ? undefined : (autopayLimit ? Number(autopayLimit) : undefined);
    if (limitNum !== undefined && (!Number.isInteger(limitNum) || limitNum < 1)) {
      setAutopayPreviewItems([]);
      setAutopayPreviewError('Limit must be a positive integer');
      return;
    }

    let cancelled = false;
    const fetchPreview = async () => {
      try {
        setIsLoadingAutopayPreview(true);
        setAutopayPreviewError(null);
        const { data, error } = await supabase.functions.invoke('autopay', {
          method: 'POST',
          body: {
            preview: true,
            ...(limitNum !== undefined ? { limit: limitNum } : {}),
            ...(autopayStudentId ? { student_id: autopayStudentId } : {}),
            ...(autopayMonth ? { period_start: `${autopayMonth}-01` } : {}),
          },
        });
        if (cancelled) return;
        if (error) throw new Error(error.message);
        const resp = data as AutopayPreviewResponse;
        const items = Array.isArray(resp.results) ? resp.results : [];
        setAutopayPreviewItems(items);
        setAutopayPlanHash(typeof resp.plan_hash === 'string' ? resp.plan_hash : null);
        // Augment enrollment names for display
        const fauxDetails: BatchResultItem[] = items.map((p) => ({ enrollment_id: p.enrollment_id, success: true, invoice_id: p.invoice_id }));
        if (fauxDetails.length > 0) {
          await augmentBatchRunDetails(fauxDetails);
        }
      } catch (e) {
        if (cancelled) return;
        setAutopayPreviewItems([]);
        setAutopayPreviewError(e instanceof Error ? e.message : 'Failed to load autopay preview');
      } finally {
        if (!cancelled) setIsLoadingAutopayPreview(false);
      }
    };

    fetchPreview();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAutopayModal, autopayLimit, autopayStudentId, autopayMonth, autopayPreviewRefreshKey]);

  async function augmentBatchRunDetails(details: BatchResultItem[]): Promise<void> {
    try {
      const missingEnrollmentIds = Array.from(new Set(details.map(d => d.enrollment_id).filter(Boolean))).filter(id => !enrollmentInfoCache.has(id));
      const missingInvoiceIds = Array.from(new Set(details.map(d => d.invoice_id).filter((v): v is string => Boolean(v)))).filter(id => !invoiceAmountCache.has(id));

      const promises: Promise<void>[] = [];

      if (missingEnrollmentIds.length > 0) {
        interface EnrollmentInfoRow {
          id: string;
          students?: { first_name?: string; last_name?: string } | null;
          programs?: { name?: string } | null;
        }
        promises.push((async () => {
          const { data } = await supabase
            .from('enrollments')
            .select('id, students(first_name,last_name), programs(name)')
            .in('id', missingEnrollmentIds);
          const map = new Map(enrollmentInfoCache);
          const rows = (data ?? []) as EnrollmentInfoRow[];
          rows.forEach((row) => {
            const student = row.students ?? undefined;
            const program = row.programs ?? undefined;
            const studentName = [student?.first_name, student?.last_name].filter(Boolean).join(' ').trim() || 'Unknown Student';
            const programName = program?.name || 'Unknown Program';
            map.set(row.id as string, { studentName, programName });
          });
          setEnrollmentInfoCache(map);
        })());
      }

      if (missingInvoiceIds.length > 0) {
        interface InvoiceAmountRow { id: string; total_amount: number }
        promises.push((async () => {
          const { data } = await supabase
            .from('invoices')
            .select('id,total_amount')
            .in('id', missingInvoiceIds);
          const map = new Map(invoiceAmountCache);
          const rows = (data ?? []) as InvoiceAmountRow[];
          rows.forEach((row) => {
            map.set(row.id, Number(row.total_amount) || 0);
          });
          setInvoiceAmountCache(map);
        })());
      }

      if (promises.length > 0) await Promise.all(promises);
    } catch {
      // Non-fatal; UI will show fallbacks
    }
  }

  // Only the newest in-flight fetch for a tab may write to state.
  //
  // Changing a filter fires a fetch, and several can overlap: clicking through
  // range presets, or typing into a custom date field, which reports '' for a
  // half-entered date. The widest query (no date bound) scans every invoice and
  // is by far the slowest, so without this it lands *after* the narrow result
  // the user asked for and silently overwrites it — the table then shows
  // unfiltered rows while the controls claim a filter is active.
  const fetchSeq = useRef({ invoices: 0, payments: 0, debt: 0 });
  function beginFetch(key: keyof typeof fetchSeq.current): () => boolean {
    const seq = ++fetchSeq.current[key];
    return () => seq === fetchSeq.current[key];
  }

  // Per-tab fetch effects. `activeTab` is in each dep array so switching tabs
  // triggers exactly one fetch (previously a separate [activeTab] effect ran
  // in parallel with these, causing every load to fire two identical requests).
  useEffect(() => {
    if (activeTab === 'payments') fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, paymentsPage, paymentSort, appliedPaymentSearch, paymentFilters.startDate, paymentFilters.endDate, paymentFilters.status, brandFilter, focusedPaymentId]);
  useEffect(() => {
    if (activeTab === 'invoices') fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, invoicesPage, invoiceSort, appliedInvoiceSearch, invoiceFilters.startDate, invoiceFilters.endDate, invoiceFilters.status, brandFilter, focusedInvoiceId]);
  useEffect(() => {
    if (activeTab === 'refunds') fetchRefunds();
  }, [activeTab]);
  useEffect(() => {
    if (activeTab === 'debt') fetchDebtInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, debtPage, debtSort, debtDaysThreshold, brandFilter]);

  async function fetchPayments() {
    const isCurrent = beginFetch('payments');
    try {
      setPaymentsLoading(true);
      // Clear any banner from a previous attempt so a retry starts clean.
      setError(null);
      let ids: string[];
      if (focusedPaymentId) {
        // A link names one payment; the list is that payment alone, whatever the date range,
        // status, brand or search box says.
        ids = [focusedPaymentId];
      } else {
        const offset = (paymentsPage - 1) * PAGE_SIZE;
        const keywords = appliedPaymentSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
        const { data: idRows, error: rpcError } = await supabase.rpc('search_payments', {
          p_keywords: keywords,
          p_start_date: paymentFilters.startDate || null,
          p_end_date: paymentFilters.endDate || null,
          p_status: paymentFilters.status || 'all',
          p_limit: PAGE_SIZE,
          p_offset: offset,
          p_sort_column: paymentSort.column,
          p_sort_direction: paymentSort.direction,
          ...brandRpcArg(brandFilter),
        });
        if (!isCurrent()) return;
        if (rpcError) throw rpcError;
        ids = (idRows || []).map((r: { payment_id: string }) => r.payment_id);
        const totalCount = (idRows && idRows[0] && typeof (idRows[0] as { total_count?: number }).total_count === 'number')
          ? (idRows[0] as { total_count: number }).total_count
          : 0;
        setPaymentsTotal(totalCount);
      }

      if (ids.length === 0) {
        setPaymentsData([]);
        setPaymentsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('payments')
        .select(`
          id,
          amount,
          payment_method,
          status,
          refunded_amount,
          created_at,
          invoice_id,
          paid_by,
          cart_id,
          payer:parents!payments_paid_by_fkey(
            id,
            first_name,
            last_name,
            email
          ),
          invoices!inner(
            id,
            period_start,
            period_end,
            enrollment_id,
            enrollments!inner(
              id,
              student_id,
              program_id,
              students!inner(id, first_name, last_name),
              programs!inner(id, name, brand_key)
            )
          )
        `)
        .in('id', ids);

      if (!isCurrent()) return;
      if (error) throw error;
      if (focusedPaymentId) setPaymentsTotal(data?.length ?? 0);

      // Preserve order from RPC
      const orderIndex = new Map<string, number>();
      ids.forEach((id, idx) => orderIndex.set(id, idx));
      const ordered = [...(data ?? [])].sort((a, b) => {
        const ia = orderIndex.get(a.id) ?? 0;
        const ib = orderIndex.get(b.id) ?? 0;
        return ia - ib;
      });

      // Format the payment data
      const formattedPayments = ordered?.map(payment => {
        // Use Record type instead of any for better type safety
        const paymentData = payment as Record<string, unknown>;
        let studentName = 'Unknown Student';
        let programName = 'Unknown Program';
        let brandKey: string | undefined;
        let periodStart: string | null = null;
        let periodEnd: string | null = null;
        let paidByParentId: string | null = null;
        let paidByParentName = '';
        let paidByParentEmail: string | null = null;

        try {
          const invoices = paymentData.invoices as Record<string, unknown>;
          if (invoices) {
            if (typeof invoices.period_start === 'string') periodStart = invoices.period_start as string;
            if (typeof invoices.period_end === 'string') periodEnd = invoices.period_end as string;
            const enrollments = invoices.enrollments as Record<string, unknown>;
            if (enrollments) {
              const students = enrollments.students as Record<string, unknown>;
              if (students && typeof students.first_name === 'string' && typeof students.last_name === 'string') {
                studentName = `${students.first_name} ${students.last_name}`;
              }
              
              const programs = enrollments.programs as Record<string, unknown>;
              if (programs && typeof programs.name === 'string') {
                programName = programs.name;
              }
              if (programs && typeof programs.brand_key === 'string') {
                brandKey = programs.brand_key;
              }
            }
          }

          // paid_by parent info
          if (typeof paymentData.paid_by === 'string') {
            paidByParentId = paymentData.paid_by;
          }
          const payer = paymentData.payer as Record<string, unknown> | undefined;
          if (payer) {
            const firstName = typeof payer.first_name === 'string' ? payer.first_name : '';
            const lastName = typeof payer.last_name === 'string' ? payer.last_name : '';
            paidByParentName = [firstName, lastName].filter(Boolean).join(' ').trim();
            paidByParentEmail = typeof payer.email === 'string' ? payer.email : null;
          }
        } catch (e) {
          console.error('Error parsing nested payment data:', e);
        }
        
        return {
          id: payment.id,
          amount: payment.amount,
          payment_method: payment.payment_method,
          status: payment.status,
          refunded_amount: payment.refunded_amount,
          student_name: studentName,
          program_name: programName,
          period_start: periodStart,
          period_end: periodEnd,
          created_at: payment.created_at,
          paid_by_parent_id: paidByParentId,
          paid_by_parent_name: paidByParentName || undefined,
          paid_by_parent_email: paidByParentEmail ?? null,
          brand_key: brandKey,
          cart_id: (paymentData.cart_id as string | null | undefined) ?? null,
        };
      }) || [];

      setPaymentsData(formattedPayments);
      setPaymentsLoading(false);
    } catch (error: unknown) {
      if (!isCurrent()) return;
      console.error('Error fetching payments:', error);
      setError(getErrorMessage(error));
      setPaymentsLoading(false);
    }
  }

  async function fetchInvoices() {
    const isCurrent = beginFetch('invoices');
    try {
      setInvoicesLoading(true);
      // Clear any banner from a previous attempt so a retry starts clean.
      setError(null);
      let ids: string[];
      if (focusedInvoiceId) {
        // A link names one invoice; the list is that invoice alone, whatever the date range,
        // status, brand or search box says.
        ids = [focusedInvoiceId];
      } else {
        const offset = (invoicesPage - 1) * PAGE_SIZE;
        const keywords = appliedInvoiceSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
        const { data: idRows, error: rpcError } = await supabase.rpc('search_invoices', {
          p_keywords: keywords,
          p_start_date: invoiceFilters.startDate || null,
          p_end_date: invoiceFilters.endDate || null,
          p_status: invoiceFilters.status || 'all',
          p_limit: PAGE_SIZE,
          p_offset: offset,
          p_sort_column: invoiceSort.column,
          p_sort_direction: invoiceSort.direction,
          ...brandRpcArg(brandFilter),
        });
        if (!isCurrent()) return;
        if (rpcError) throw rpcError;
        ids = (idRows || []).map((r: { invoice_id: string }) => r.invoice_id);
        const totalCount = (idRows && idRows[0] && typeof (idRows[0] as { total_count?: number }).total_count === 'number')
          ? (idRows[0] as { total_count: number }).total_count
          : 0;
        setInvoicesTotal(totalCount);
      }

      if (ids.length === 0) {
        setInvoicesData([]);
        await checkInvoiceIdsForLateFees([]);
        setInvoicesLoading(false);
        return;
      }

      // Stage 2 (detail fetch) and Stage 3 (late-fee check) are independent
      // once we have the IDs, so run them in parallel instead of sequentially.
      const [{ data, error }] = await Promise.all([
        supabase
          .from('invoices')
          .select(`
            id,
            total_amount,
            amount_paid,
            status,
            due_date,
            period_start,
            period_end,
            created_at,
            updated_at,
            memo_code,
            email_suppressed,
            enrollment_id,
            enrollments!inner(
              id,
              student_id,
              program_id,
              enrolled_by,
              enrolled_by_parent:parents!enrolled_by(
                id,
                first_name,
                last_name,
                email
              ),
              students!inner(id, first_name, last_name),
              programs!inner(id, name, brand_key)
            ),
            payments(status)
          `)
          .in('id', ids),
        checkInvoiceIdsForLateFees(ids),
      ]);

      if (!isCurrent()) return;
      if (error) throw error;
      if (focusedInvoiceId) setInvoicesTotal(data?.length ?? 0);

      // Preserve order from RPC
      const orderIndex = new Map<string, number>();
      ids.forEach((id, idx) => orderIndex.set(id, idx));
      const ordered = [...(data ?? [])].sort((a, b) => {
        const ia = orderIndex.get(a.id) ?? 0;
        const ib = orderIndex.get(b.id) ?? 0;
        return ia - ib;
      });

      // A bank bundle payment still clearing has no payments row yet; it is in flight too.
      const heldByCart = new Set(await fetchCartHeldInvoiceIds());

      // Format the invoice data — fetchInvoices
      const formattedInvoices = ordered?.map(invoice => {
        // Use Record type instead of any for better type safety
        const invoiceData = invoice as Record<string, unknown>;
        let studentName = 'Unknown Student';
        let programName = 'Unknown Program';
        let brandKey: string | undefined;
        let enrolledByParentName = '';
        let enrolledByParentEmail: string | null = null;

        try {
          const enrollments = invoiceData.enrollments as Record<string, unknown>;
          if (enrollments) {
            const students = enrollments.students as Record<string, unknown>;
            if (students && typeof students.first_name === 'string' && typeof students.last_name === 'string') {
              studentName = `${students.first_name} ${students.last_name}`;
            }
            
            const programs = enrollments.programs as Record<string, unknown>;
            if (programs && typeof programs.name === 'string') {
              programName = programs.name;
            }
            if (programs && typeof programs.brand_key === 'string') {
              brandKey = programs.brand_key;
            }

            const enrolledByParent = enrollments.enrolled_by_parent as Record<string, unknown> | undefined;
            if (enrolledByParent) {
              const fn = typeof enrolledByParent.first_name === 'string' ? enrolledByParent.first_name : '';
              const ln = typeof enrolledByParent.last_name === 'string' ? enrolledByParent.last_name : '';
              enrolledByParentName = [fn, ln].filter(Boolean).join(' ').trim();
              enrolledByParentEmail = typeof enrolledByParent.email === 'string' ? enrolledByParent.email : null;
            }
          }
        } catch (e) {
          console.error('Error parsing nested invoice data:', e);
        }

        const payments = (invoice.payments || []) as { status: string }[];
        const isPaymentProcessing = isPaymentInFlight({ id: invoice.id, payments }, heldByCart);

        return {
          id: invoice.id,
          total_amount: invoice.total_amount,
          amount_paid: invoice.amount_paid,
          status: isPaymentProcessing ? 'payment_processing' : invoice.status,
          due_date: invoice.due_date,
          period_start: invoice.period_start,
          period_end: invoice.period_end,
          student_name: studentName,
          program_name: programName,
          created_at: invoice.created_at,
          updated_at: invoice.updated_at,
          memo_code: invoice.memo_code,
          enrolled_by_parent_name: enrolledByParentName || undefined,
          enrolled_by_parent_email: enrolledByParentEmail ?? null,
          brand_key: brandKey,
          email_suppressed: Boolean(invoiceData.email_suppressed),
        };
      }) || [];

      setInvoicesData(formattedInvoices);
      setInvoicesLoading(false);
    } catch (error: unknown) {
      if (!isCurrent()) return;
      console.error('Error fetching invoices:', error);
      setError(getErrorMessage(error));
      setInvoicesLoading(false);
    }
  }

  // Refresh specific invoice data in place
  async function refreshInvoiceData(invoiceId: string) {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id,
          total_amount,
          amount_paid,
          status,
          due_date,
          period_start,
          period_end,
          created_at,
          updated_at,
          memo_code,
          email_suppressed,
          enrollment_id,
          enrollments!inner(
            id,
            student_id,
            program_id,
            enrolled_by,
            enrolled_by_parent:parents!enrolled_by(
              id,
              first_name,
              last_name,
              email
            ),
            students!inner(id, first_name, last_name),
            programs!inner(id, name, brand_key)
          ),
          payments(status)
        `)
        .eq('id', invoiceId)
        .single();

      if (error || !data) return;

      // Format the invoice data (same logic as fetchInvoices)
      const invoiceData = data as Record<string, unknown>;
      let studentName = 'Unknown Student';
      let programName = 'Unknown Program';
      let brandKey: string | undefined;
      let enrolledByParentName = '';
      let enrolledByParentEmail: string | null = null;

      try {
        const enrollments = invoiceData.enrollments as Record<string, unknown>;
        if (enrollments) {
          const students = enrollments.students as Record<string, unknown>;
          if (students && typeof students.first_name === 'string' && typeof students.last_name === 'string') {
            studentName = `${students.first_name} ${students.last_name}`;
          }
          
          const programs = enrollments.programs as Record<string, unknown>;
          if (programs && typeof programs.name === 'string') {
            programName = programs.name;
          }
          if (programs && typeof programs.brand_key === 'string') {
            brandKey = programs.brand_key;
          }

          const enrolledByParent = enrollments.enrolled_by_parent as Record<string, unknown> | undefined;
          if (enrolledByParent) {
            const fn = typeof enrolledByParent.first_name === 'string' ? enrolledByParent.first_name : '';
            const ln = typeof enrolledByParent.last_name === 'string' ? enrolledByParent.last_name : '';
            enrolledByParentName = [fn, ln].filter(Boolean).join(' ').trim();
            enrolledByParentEmail = typeof enrolledByParent.email === 'string' ? enrolledByParent.email : null;
          }
        }
      } catch (e) {
        console.error('Error parsing nested invoice data:', e);
      }

      const payments = (data.payments || []) as { status: string }[];
      const heldByCart = new Set(await fetchCartHeldInvoiceIds(invoiceId));
      const isPaymentProcessing = isPaymentInFlight({ id: data.id, payments }, heldByCart);
      
      const updatedInvoice: InvoiceData = {
        id: data.id,
        total_amount: data.total_amount,
        amount_paid: data.amount_paid,
        status: isPaymentProcessing ? 'payment_processing' : data.status,
        due_date: data.due_date,
        period_start: data.period_start,
        period_end: data.period_end,
        student_name: studentName,
        program_name: programName,
        created_at: data.created_at,
        updated_at: data.updated_at,
        memo_code: data.memo_code,
        enrolled_by_parent_name: enrolledByParentName || undefined,
        enrolled_by_parent_email: enrolledByParentEmail ?? null,
        brand_key: brandKey,
        email_suppressed: Boolean((data as Record<string, unknown>).email_suppressed),
      };

      // Update the specific invoice in the existing data
      setInvoicesData(prev => 
        prev.map(invoice => 
          invoice.id === invoiceId ? updatedInvoice : invoice
        )
      );

      // Also check if this invoice has late fees
      await checkInvoicesForLateFees([updatedInvoice]);
    } catch (error) {
      console.error('Error refreshing invoice data:', error);
    }
  }

  // Check which invoices have late fees
  async function checkInvoicesForLateFees(invoices: InvoiceData[]) {
    return checkInvoiceIdsForLateFees(invoices.map(invoice => invoice.id));
  }

  async function checkInvoiceIdsForLateFees(invoiceIds: string[]) {
    try {
      if (invoiceIds.length === 0) {
        setInvoicesWithLateFees(new Set());
        return;
      }

      const { data, error } = await supabase
        .from('invoicing_line_items')
        .select(`
          invoice_id,
          description,
          amount,
          line_item_configs!inner(name)
        `)
        .in('invoice_id', invoiceIds)
        .eq('line_item_configs.name', 'Late Fee');

      if (error) {
        console.error('Error checking for late fees:', error);
        return;
      }

      const firstLateFeeInvoiceIds = new Set<string>();
      const secondLateFeeInvoiceIds = new Set<string>();

      // Categorize invoices based on late fee description
      data?.forEach(item => {
        if (item.description === 'First late fee') {
          firstLateFeeInvoiceIds.add(item.invoice_id);
        } else if (item.description === 'Second time late fee') {
          secondLateFeeInvoiceIds.add(item.invoice_id);
        }
      });

      setInvoicesWithLateFees(firstLateFeeInvoiceIds);
      setInvoicesWithSecondLateFees(secondLateFeeInvoiceIds);
    } catch (error) {
      console.error('Error checking for late fees:', error);
    }
  }

  async function fetchRefunds() {
    try {
      setRefundsLoading(true);
      // Clear any banner from a previous attempt so a retry starts clean.
      setError(null);
      const { data, error } = await supabase
        .from('refunds')
        .select(`
          id,
          amount,
          refund_date,
          status,
          refund_method,
          manual_refund_notes,
          created_at,
          payment_id,
          payments!inner(
            invoice_id,
            amount,
            payment_method,
            paid_by,
            invoices!inner(
              period_start,
              period_end,
              enrollment_id,
              enrollments!inner(
                student_id,
                program_id,
                students!inner(id, first_name, last_name),
                programs!inner(id, name, brand_key)
              )
            ),
            payer:parents!payments_paid_by_fkey(
              id,
              first_name,
              last_name,
              email
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Format the refund data
      const formattedRefunds = data?.map(refund => {
        // Use Record type instead of any for better type safety
        const refundData = refund as Record<string, unknown>;
        let studentName = 'Unknown Student';
        let programName = 'Unknown Program';
        let brandKey: string | undefined;
        let periodStart: string | null = null;
        let periodEnd: string | null = null;
        let originalPaymentAmount = 0;
        let originalPaymentMethod = '';
        let paidByParentId: string | null = null;
        let paidByParentName = '';
        let paidByParentEmail: string | null = null;

        try {
          const payments = refundData.payments as Record<string, unknown>;
          if (payments) {
            originalPaymentAmount = typeof payments.amount === 'number' ? payments.amount : 0;
            originalPaymentMethod = typeof payments.payment_method === 'string' ? payments.payment_method : '';
            
            const invoices = payments.invoices as Record<string, unknown>;
            if (invoices) {
              if (typeof invoices.period_start === 'string') periodStart = invoices.period_start as string;
              if (typeof invoices.period_end === 'string') periodEnd = invoices.period_end as string;
              const enrollments = invoices.enrollments as Record<string, unknown>;
              if (enrollments) {
                const students = enrollments.students as Record<string, unknown>;
                if (students && typeof students.first_name === 'string' && typeof students.last_name === 'string') {
                  studentName = `${students.first_name} ${students.last_name}`;
                }
                
                const programs = enrollments.programs as Record<string, unknown>;
                if (programs && typeof programs.name === 'string') {
                  programName = programs.name;
                }
                if (programs && typeof programs.brand_key === 'string') {
                  brandKey = programs.brand_key;
                }
              }
            }

            // paid_by parent info
            if (typeof payments.paid_by === 'string') {
              paidByParentId = payments.paid_by;
            }
            const payer = payments.payer as Record<string, unknown> | undefined;
            if (payer) {
              const firstName = typeof payer.first_name === 'string' ? payer.first_name : '';
              const lastName = typeof payer.last_name === 'string' ? payer.last_name : '';
              paidByParentName = [firstName, lastName].filter(Boolean).join(' ').trim();
              paidByParentEmail = typeof payer.email === 'string' ? payer.email : null;
            }
          }
        } catch (e) {
          console.error('Error parsing nested refund data:', e);
        }
        
        return {
          id: refund.id,
          payment_id: refund.payment_id,
          amount: refund.amount,
          student_name: studentName,
          program_name: programName,
          period_start: periodStart,
          period_end: periodEnd,
          refund_method: refund.refund_method,
          status: refund.status,
          refund_date: refund.refund_date,
          created_at: refund.created_at,
          original_payment_amount: originalPaymentAmount,
          original_payment_method: originalPaymentMethod,
          refund_notes: refund.manual_refund_notes,
          paid_by_parent_id: paidByParentId,
          paid_by_parent_name: paidByParentName || undefined,
          paid_by_parent_email: paidByParentEmail ?? null,
          brand_key: brandKey,
        };
      }) || [];

      setRefundsData(formattedRefunds);
      setRefundsLoading(false);
    } catch (error: unknown) {
      console.error('Error fetching refunds:', error);
      setError(getErrorMessage(error));
      setRefundsLoading(false);
    }
  }

  // Fetch invoices N+ days overdue for the Debt tab (configurable threshold)
  async function fetchDebtInvoices() {
    const isCurrent = beginFetch('debt');
    try {
      setDebtLoading(true);
      // Clear any banner from a previous attempt so a retry starts clean.
      setError(null);
      const offset = (debtPage - 1) * PAGE_SIZE;

      // Calculate the date N days ago based on threshold
      const thresholdDate = new Date();
      thresholdDate.setDate(thresholdDate.getDate() - debtDaysThreshold);
      const thresholdDateStr = thresholdDate.toISOString().split('T')[0];

      // Build query for overdue invoices (N+ days past due, not paid/voided)
      let query = supabase
        .from('invoices')
        .select(`
          id,
          total_amount,
          amount_paid,
          status,
          due_date,
          period_start,
          period_end,
          created_at,
          updated_at,
          memo_code,
          enrollment_id,
          enrollments!inner(
            id,
            student_id,
            program_id,
            enrolled_by,
            enrolled_by_parent:parents!enrolled_by(
              id,
              first_name,
              last_name,
              email
            ),
            students!inner(id, first_name, last_name),
            programs!inner(id, name, brand_key)
          ),
          payments(status)
        `, { count: 'exact' })
        .lte('due_date', thresholdDateStr)
        .in('status', ['pending', 'partial', 'overdue'])
        .range(offset, offset + PAGE_SIZE - 1);

      // Apply sorting
      if (debtSort.column === 'student_name') {
        query = query.order('enrollments(students(last_name))', { ascending: debtSort.direction === 'asc' });
      } else if (debtSort.column === 'program_name') {
        query = query.order('enrollments(programs(name))', { ascending: debtSort.direction === 'asc' });
      } else if (debtSort.column === 'amount') {
        query = query.order('total_amount', { ascending: debtSort.direction === 'asc' });
      } else if (debtSort.column === 'due_date') {
        query = query.order('due_date', { ascending: debtSort.direction === 'asc' });
      } else {
        query = query.order('created_at', { ascending: debtSort.direction === 'asc' });
      }

      // Both embeds are !inner, so this drops the invoice rather than nulling its program.
      if (brandFilter !== 'all') {
        query = query.eq('enrollments.programs.brand_key', brandFilter);
      }

      const { data, error, count } = await query;

      if (!isCurrent()) return;
      if (error) throw error;

      setDebtTotal(count || 0);

      // A bank bundle payment still clearing has no payments row yet; it is in flight too.
      const heldByCart = new Set(await fetchCartHeldInvoiceIds());

      // Format the invoice data
      const formattedInvoices = data?.map(invoice => {
        const invoiceData = invoice as Record<string, unknown>;
        let studentName = 'Unknown Student';
        let programName = 'Unknown Program';
        let brandKey: string | undefined;
        let enrolledByParentName = '';
        let enrolledByParentEmail: string | null = null;

        try {
          const enrollments = invoiceData.enrollments as Record<string, unknown>;
          if (enrollments) {
            const students = enrollments.students as Record<string, unknown>;
            if (students && typeof students.first_name === 'string' && typeof students.last_name === 'string') {
              studentName = `${students.first_name} ${students.last_name}`;
            }
            
            const programs = enrollments.programs as Record<string, unknown>;
            if (programs && typeof programs.name === 'string') {
              programName = programs.name;
            }
            if (programs && typeof programs.brand_key === 'string') {
              brandKey = programs.brand_key;
            }

            const enrolledByParent = enrollments.enrolled_by_parent as Record<string, unknown> | undefined;
            if (enrolledByParent) {
              const fn = typeof enrolledByParent.first_name === 'string' ? enrolledByParent.first_name : '';
              const ln = typeof enrolledByParent.last_name === 'string' ? enrolledByParent.last_name : '';
              enrolledByParentName = [fn, ln].filter(Boolean).join(' ').trim();
              enrolledByParentEmail = typeof enrolledByParent.email === 'string' ? enrolledByParent.email : null;
            }
          }
        } catch (e) {
          console.error('Error parsing nested invoice data:', e);
        }

        const payments = (invoice.payments || []) as { status: string }[];
        const isPaymentProcessing = isPaymentInFlight({ id: invoice.id, payments }, heldByCart);

        return {
          id: invoice.id,
          total_amount: invoice.total_amount,
          amount_paid: invoice.amount_paid,
          status: isPaymentProcessing ? 'payment_processing' : invoice.status,
          due_date: invoice.due_date,
          period_start: invoice.period_start,
          period_end: invoice.period_end,
          student_name: studentName,
          program_name: programName,
          created_at: invoice.created_at,
          updated_at: invoice.updated_at,
          memo_code: invoice.memo_code,
          enrolled_by_parent_name: enrolledByParentName || undefined,
          enrolled_by_parent_email: enrolledByParentEmail ?? null,
          brand_key: brandKey,
        };
      }) || [];

      // Exclude invoices where payment is already processing (e.g. ACH in progress)
      const filteredInvoices = formattedInvoices.filter(inv => inv.status !== 'payment_processing');

      setDebtData(filteredInvoices);
      setDebtLoading(false);
    } catch (error: unknown) {
      if (!isCurrent()) return;
      console.error('Error fetching debt invoices:', error);
      setError(getErrorMessage(error));
      setDebtLoading(false);
    }
  }

  // Handle voiding an invoice
  async function handleVoidInvoice(invoice: InvoiceData) {
    if (!confirm(`Are you sure you want to void this invoice for ${invoice.student_name} - ${invoice.program_name}? This will mark the invoice as uncollectible.`)) {
      return;
    }

    try {
      setVoidingInvoice(invoice.id);
      
      const { error } = await supabase
        .from('invoices')
        .update({ 
          status: 'voided',
          updated_at: new Date().toISOString()
        })
        .eq('id', invoice.id);

      if (error) throw error;

      trackEvent({ eventType: 'invoice_voided', user: currentUser, entityType: 'invoice', entityId: invoice.id, metadata: { ...invoiceSubject(invoice), amount: invoice.total_amount } });

      // Refresh debt data
      await fetchDebtInvoices();
    } catch (error: unknown) {
      console.error('Error voiding invoice:', error);
      alert(error instanceof Error ? error.message : 'Failed to void invoice');
    } finally {
      setVoidingInvoice(null);
    }
  }

  // Comp an invoice to $0 for a no-charge student. Unlike voiding, this appends
  // a labelled discount line and keeps the original line items, so preflight
  // records and roster flags that read line items stay intact.
  async function handleCompInvoice(invoice: InvoiceData) {
    const balance = Math.round((invoice.total_amount - (invoice.amount_paid ?? 0)) * 100) / 100;
    if (!confirm(`Comp this invoice for ${invoice.student_name} - ${invoice.program_name}? A "Comp — No Charge" discount of $${balance.toFixed(2)} will be added and the invoice marked paid.`)) {
      return;
    }

    try {
      setCompingInvoice(invoice.id);

      const { error } = await supabase.rpc('comp_invoice', {
        p_invoice_id: invoice.id,
        p_note: null,
      });

      if (error) throw error;

      trackEvent({ eventType: 'invoice_comped', user: currentUser, entityType: 'invoice', entityId: invoice.id, metadata: { ...invoiceSubject(invoice), comped_amount: balance, previous_status: invoice.status } });

      await fetchInvoices();
    } catch (error: unknown) {
      console.error('Error comping invoice:', error);
      alert(error instanceof Error ? error.message : 'Failed to comp invoice');
    } finally {
      setCompingInvoice(null);
    }
  }

  // Send the invoice-created email for an invoice that had email suppressed
  async function handleSendInvoiceEmail(invoice: InvoiceData) {
    try {
      setSendingInvoiceEmail(invoice.id);

      // Fetch all active parents of the student via invoice → enrollment → student_parents
      let recipientEmails: string[] = [];
      const { data: invoiceRow } = await supabase
        .from('invoices')
        .select('enrollment_id')
        .eq('id', invoice.id)
        .single();
      const enrollmentId = (invoiceRow as { enrollment_id?: string } | null)?.enrollment_id;
      if (enrollmentId) {
        const { data: enrollmentRow } = await supabase
          .from('enrollments')
          .select('student_id')
          .eq('id', enrollmentId)
          .single();
        const studentId = (enrollmentRow as { student_id?: string } | null)?.student_id;
        if (studentId) {
          const { data: parentRows } = await supabase
            .from('student_parents')
            .select('parents!inner(email, active)')
            .eq('student_id', studentId)
            .eq('parents.active', true);
          recipientEmails = ((parentRows ?? []) as Array<{ parents: unknown }>)
            .flatMap((r) => {
              const p = r.parents as { email?: string }[] | { email?: string } | null;
              if (!p) return [];
              const emails = Array.isArray(p) ? p.map((pp) => pp.email) : [p.email];
              return emails.filter((e): e is string => Boolean(e));
            });
        }
      }
      // Fallback to enrolled_by parent if student_parents query returns nothing
      if (recipientEmails.length === 0 && invoice.enrolled_by_parent_email) {
        recipientEmails = [invoice.enrolled_by_parent_email];
      }
      if (recipientEmails.length === 0) {
        setReminderToast({ message: 'No parent email found for this invoice', type: 'error' });
        return;
      }

      // Fetch line items fresh from DB so the email reflects any edits
      const { data: lineItemRows, error: liError } = await supabase
        .from('invoicing_line_items')
        .select('description, amount')
        .eq('invoice_id', invoice.id);
      if (liError) throw liError;

      const lineItemsJson = JSON.stringify(
        (lineItemRows ?? []).map((li: { description: string | null; amount: number }) => ({
          description: li.description,
          amount: li.amount,
        }))
      );

      const programPeriod = formatPeriodRange(invoice.period_start, invoice.period_end);

      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'template',
          templateKey: 'invoice-created',
          brand: invoice.brand_key,
          to: recipientEmails,
          data: {
            invoiceId: invoice.id,
            studentName: invoice.student_name,
            programName: invoice.program_name,
            amount: invoice.total_amount.toFixed(2),
            dueDate: formatDateDisplay(invoice.due_date),
            invoicePeriod: programPeriod,
            memoCode: invoice.memo_code ?? 'N/A',
            lineItems: lineItemsJson,
          },
        },
      });
      if (error) throw error;

      // Clear the suppression flag so the button disappears
      await supabase.from('invoices').update({ email_suppressed: false }).eq('id', invoice.id);

      trackEvent({ eventType: 'invoice_email_sent', user: currentUser, entityType: 'invoice', entityId: invoice.id, metadata: { ...invoiceSubject(invoice), recipient: recipientEmails.join(', ') } });
      setReminderToast({ message: `Invoice email sent to ${recipientEmails.join(', ')}`, type: 'success' });
      await refreshInvoiceData(invoice.id);
    } catch (error: unknown) {
      console.error('Error sending invoice email:', error);
      setReminderToast({ message: error instanceof Error ? error.message : 'Failed to send invoice email', type: 'error' });
    } finally {
      setSendingInvoiceEmail(null);
    }
  }

  // Handle sending payment reminder to a single invoice
  async function handleSendPaymentReminder(invoice: InvoiceData) {
    if (!invoice.enrolled_by_parent_email) {
      setReminderToast({ message: 'No parent email found for this invoice', type: 'error' });
      return;
    }

    try {
      setSendingReminder(invoice.id);
      
      const daysOverdue = Math.floor((new Date().getTime() - new Date(invoice.due_date).getTime()) / (1000 * 60 * 60 * 24));
      const remainingAmount = invoice.total_amount - invoice.amount_paid;
      const programPeriod = formatPeriodRange(invoice.period_start, invoice.period_end);
      
      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'template',
          templateKey: 'payment-reminder',
          brand: invoice.brand_key,
          to: invoice.enrolled_by_parent_email,
          data: {
            studentName: invoice.student_name,
            programName: invoice.program_name,
            programPeriod: programPeriod,
            memoCode: invoice.memo_code || 'N/A',
            amountDue: remainingAmount.toFixed(2),
            totalAmount: invoice.total_amount.toFixed(2),
            amountPaid: invoice.amount_paid.toFixed(2),
            dueDate: formatDateDisplay(invoice.due_date),
            daysOverdue: String(daysOverdue),
            paymentUrl: 'https://portal.bhcskids.com/payments',
            policyUrl: BILLING_POLICY_URL,
          }
        }
      });

      if (error) throw error;

      trackEvent({ eventType: 'payment_reminder_sent', user: currentUser, entityType: 'invoice', entityId: invoice.id, metadata: { ...invoiceSubject(invoice), amount: remainingAmount, days_overdue: daysOverdue } });

      setReminderToast({ 
        message: `Payment reminder sent to ${invoice.enrolled_by_parent_email}`, 
        type: 'success' 
      });
    } catch (error: unknown) {
      console.error('Error sending payment reminder:', error);
      setReminderToast({ 
        message: error instanceof Error ? error.message : 'Failed to send payment reminder', 
        type: 'error' 
      });
    } finally {
      setSendingReminder(null);
    }
  }

  // Handle sending the late fee notice for an invoice that already carries a late fee.
  // Mirrors the payment reminder, then adds the fee that was charged, what happens if the
  // balance stays unpaid, and a link to the Enrollment & Billing Guide.
  async function handleSendLateFeeNotice(invoice: InvoiceData) {
    if (!invoice.enrolled_by_parent_email) {
      setReminderToast({ message: 'No parent email found for this invoice', type: 'error' });
      return;
    }

    try {
      setSendingLateFeeNotice(invoice.id);

      const { data: lateFeeItems, error: lineItemError } = await supabase
        .from('invoicing_line_items')
        .select(`
          amount,
          line_item_configs!inner(name)
        `)
        .eq('invoice_id', invoice.id)
        .eq('line_item_configs.name', 'Late Fee');

      if (lineItemError) throw lineItemError;

      const lateFeeAmount = lateFeeItems?.reduce((sum, item) => sum + item.amount, 0) || 0;
      if (lateFeeAmount <= 0) {
        setReminderToast({ message: 'Add a late fee to this invoice before sending the notice', type: 'error' });
        return;
      }

      const isSecondTier = invoicesWithSecondLateFees.has(invoice.id);
      const rate = isSecondTier ? LATE_FEE.secondRate : LATE_FEE.firstRate;
      const daysOverdue = Math.floor((new Date().getTime() - new Date(invoice.due_date).getTime()) / (1000 * 60 * 60 * 24));
      const remainingAmount = invoice.total_amount - invoice.amount_paid;
      // The balance the fee was charged against, i.e. before the fee itself was added
      const lateFeeBase = Math.max(remainingAmount - lateFeeAmount, 0);
      const programPeriod = formatPeriodRange(invoice.period_start, invoice.period_end);
      const escalationText = isSecondTier
        ? `${formatLateFeeRate(LATE_FEE.secondRate)} is the highest late fee under our published schedule, but the balance remains due in full.`
        : `If the balance is still unpaid on the 10th, the late fee increases to ${formatLateFeeRate(LATE_FEE.secondRate)} of the outstanding balance.`;

      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'template',
          templateKey: 'late-fee-notice',
          brand: invoice.brand_key,
          to: invoice.enrolled_by_parent_email,
          data: {
            invoiceId: invoice.id,
            studentName: invoice.student_name,
            programName: invoice.program_name,
            programPeriod: programPeriod,
            memoCode: invoice.memo_code || 'N/A',
            amountDue: remainingAmount.toFixed(2),
            totalAmount: invoice.total_amount.toFixed(2),
            amountPaid: invoice.amount_paid.toFixed(2),
            dueDate: formatDateDisplay(invoice.due_date),
            daysOverdue: String(daysOverdue),
            lateFeeRate: formatLateFeeRate(rate),
            lateFeeAmount: lateFeeAmount.toFixed(2),
            lateFeeBase: lateFeeBase.toFixed(2),
            escalationText,
            paymentUrl: 'https://portal.bhcskids.com/payments',
            policyUrl: BILLING_POLICY_URL,
          },
        },
      });

      if (error) throw error;

      trackEvent({
        eventType: 'late_fee_notice_sent',
        user: currentUser,
        userType: 'admin',
        entityType: 'invoice',
        entityId: invoice.id,
        metadata: { late_fee_amount: lateFeeAmount, late_fee_rate: rate },
        success: true,
      });

      setReminderToast({
        message: `Late fee notice sent to ${invoice.enrolled_by_parent_email}`,
        type: 'success',
      });
    } catch (error: unknown) {
      console.error('Error sending late fee notice:', error);
      setReminderToast({
        message: error instanceof Error ? error.message : 'Failed to send late fee notice',
        type: 'error',
      });
    } finally {
      setSendingLateFeeNotice(null);
    }
  }

  // Handle sending payment reminders to all invoices in debt list
  async function handleSendBulkPaymentReminders() {
    const invoicesWithEmail = debtData.filter(inv => inv.enrolled_by_parent_email);
    
    if (invoicesWithEmail.length === 0) {
      setReminderToast({ message: 'No invoices with parent email addresses found', type: 'error' });
      return;
    }

    if (!confirm(`Send payment reminder emails to ${invoicesWithEmail.length} parent(s)? This will email all parents with overdue invoices currently displayed.`)) {
      return;
    }

    try {
      setSendingBulkReminders(true);
      let successCount = 0;
      let failCount = 0;

      for (const invoice of invoicesWithEmail) {
        try {
          const daysOverdue = Math.floor((new Date().getTime() - new Date(invoice.due_date).getTime()) / (1000 * 60 * 60 * 24));
          const remainingAmount = invoice.total_amount - invoice.amount_paid;
          const programPeriod = formatPeriodRange(invoice.period_start, invoice.period_end);
          
          const { error } = await supabase.functions.invoke('send-email', {
            body: {
              type: 'template',
              templateKey: 'payment-reminder',
              brand: invoice.brand_key,
              to: invoice.enrolled_by_parent_email,
              data: {
                studentName: invoice.student_name,
                programName: invoice.program_name,
                programPeriod: programPeriod,
                memoCode: invoice.memo_code || 'N/A',
                amountDue: remainingAmount.toFixed(2),
                totalAmount: invoice.total_amount.toFixed(2),
                amountPaid: invoice.amount_paid.toFixed(2),
                dueDate: formatDateDisplay(invoice.due_date),
                daysOverdue: String(daysOverdue),
                paymentUrl: 'https://portal.bhcskids.com/payments',
                policyUrl: BILLING_POLICY_URL,
              }
            }
          });

          if (error) {
            failCount++;
            console.error(`Failed to send reminder for invoice ${invoice.id}:`, error);
          } else {
            successCount++;
            trackEvent({ eventType: 'payment_reminder_sent', user: currentUser, entityType: 'invoice', entityId: invoice.id, metadata: { ...invoiceSubject(invoice), amount: remainingAmount, days_overdue: daysOverdue } });
          }
        } catch {
          failCount++;
        }
      }

      if (failCount === 0) {
        setReminderToast({ 
          message: `Successfully sent ${successCount} payment reminder(s)`, 
          type: 'success' 
        });
      } else {
        setReminderToast({ 
          message: `Sent ${successCount} reminders, ${failCount} failed`, 
          type: successCount > 0 ? 'success' : 'error' 
        });
      }
    } catch (error: unknown) {
      console.error('Error sending bulk payment reminders:', error);
      setReminderToast({ 
        message: error instanceof Error ? error.message : 'Failed to send payment reminders', 
        type: 'error' 
      });
    } finally {
      setSendingBulkReminders(false);
    }
  }

  function handleOpenRefundModal(payment: PaymentData) {
    setRefundModal({
      isOpen: true,
      paymentId: payment.id,
      paymentAmount: payment.amount,
      studentName: payment.student_name,
      programName: payment.program_name,
      amount: payment.amount - (payment.refunded_amount || 0), // default to full remaining amount
      isProcessing: false,
      error: null,
      paymentMethod: payment.payment_method,
      refundMethod: payment.payment_method === 'card' ? 'card' : 'manual',
      refundNotes: '',
      refunded_amount: payment.refunded_amount,
      parentId: payment.paid_by_parent_id ?? null,
      bucket: 'non_withdrawable',
      cartId: payment.cart_id ?? null,
    });
  }

  // From CartDetailModal: one invoice's share, refunded through the usual refund modal.
  function handleRefundCartPayment(payment: CartPaymentForRefund) {
    setOpenCartId(null);
    handleOpenRefundModal({
      id: payment.id,
      amount: payment.amount,
      refunded_amount: payment.refunded_amount,
      payment_method: payment.payment_method,
      status: payment.status,
      created_at: payment.created_at,
      student_name: payment.student_name,
      program_name: payment.program_name,
      paid_by_parent_id: payment.paid_by_parent_id,
      cart_id: payment.cart_id,
    });
  }

  function handleCloseRefundModal() {
    setRefundModal({
      isOpen: false,
      paymentId: null,
      paymentAmount: 0,
      studentName: '',
      programName: '',
      amount: 0,
      isProcessing: false,
      error: null,
      paymentMethod: '',
      refundMethod: '',
      refundNotes: '',
      refunded_amount: 0,
      parentId: null,
      bucket: 'non_withdrawable',
    });
  }

  function handleOpenManualPaymentModal(invoice: InvoiceData) {
    const remainingAmount = invoice.total_amount - invoice.amount_paid;
    setManualPaymentModal({
      isOpen: true,
      invoiceId: invoice.id,
      studentName: invoice.student_name,
      programName: invoice.program_name,
      totalAmount: invoice.total_amount,
      amountPaid: invoice.amount_paid,
      remainingAmount: remainingAmount,
      paymentAmount: remainingAmount, // Default to full remaining amount
      paymentMethod: 'check',
      paymentDate: getTodayDateStringInBusinessTZ(),
      isProcessing: false,
      error: null
    });
  }

  function handleCloseManualPaymentModal() {
    setManualPaymentModal({
      isOpen: false,
      invoiceId: null,
      studentName: '',
      programName: '',
      totalAmount: 0,
      amountPaid: 0,
      remainingAmount: 0,
      paymentAmount: 0,
      paymentMethod: 'check',
      paymentDate: getTodayDateStringInBusinessTZ(),
      isProcessing: false,
      error: null
    });
  }

  async function handleProcessRefund() {
    if (!refundModal.paymentId) return;

    try {
      setRefundModal({ ...refundModal, isProcessing: true, error: null });

      // Validate refund amount
      if (refundModal.amount <= 0) {
        throw new Error('Refund amount must be greater than zero');
      }

      const remainingAmount = refundModal.paymentAmount - (refundModal.refunded_amount || 0);
      if (refundModal.amount > remainingAmount) {
        throw new Error(`Cannot refund more than the remaining amount: $${remainingAmount.toFixed(2)}`);
      }

      // For Stripe payments, use the process-refund endpoint
      if (refundModal.paymentMethod === 'card' && refundModal.refundMethod === 'card') {
        // The charge's PaymentIntent, from stripe_transactions where the old checkout recorded it.
        // A bundle payment records none; process-refund then uses the payment's own
        // stripe_payment_id, which is the record anyway.
        const { data: stripeTransaction, error: stripeError } = await supabase
          .from('stripe_transactions')
          .select('payment_intent_id')
          .eq('payment_id', refundModal.paymentId)
          .eq('action', 'charge') // Only get charge transactions, not refunds
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (stripeError) {
          throw new Error('Failed to retrieve payment information for refund');
        }

        // Get current session for authentication
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;
        
        if (!accessToken) {
          throw new Error('Authentication error: No valid session');
        }

        // Call the process-refund Supabase Edge Function
        try {
          // Use the VITE_SUPABASE_URL environment variable
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const functionUrl = `${supabaseUrl}/functions/v1/process-refund`;
          
          console.log("Calling Supabase function at:", functionUrl);
          console.log("Request payload:", {
            paymentId: refundModal.paymentId,
            paymentIntentId: stripeTransaction?.payment_intent_id ?? null,
            amount: Math.round(refundModal.amount * 100),
            reason: 'requested_by_customer',
            manualRefundNotes: refundModal.refundNotes,
            refundMethod: 'card'
          });
          
          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
              paymentId: refundModal.paymentId,
              paymentIntentId: stripeTransaction?.payment_intent_id ?? undefined,
              amount: Math.round(refundModal.amount * 100), // Convert to cents for Stripe
              reason: 'requested_by_customer', // Valid Stripe reason value
              manualRefundNotes: refundModal.refundNotes, // Store the detailed notes separately
              refundMethod: 'card'
            })
          });

          console.log("Response status:", response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error("Error response:", errorText);
            
            let errorMessage = 'Failed to process refund';
            try {
              if (errorText) {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.error || errorMessage;
              }
            } catch (parseError) {
              console.error("Error parsing JSON response:", parseError);
            }
            
            throw new Error(errorMessage);
          }

          const responseData = await response.json();
          console.log("Refund processed successfully:", responseData);
        } catch (error: unknown) {
          console.error("Fetch error:", error);
          throw new Error(`Error processing refund: ${getErrorMessage(error)}`);
        }
      } else if (refundModal.refundMethod === 'wallet') {
        // Refund to BHCS CASH wallet via balance-admin-refund-to-wallet edge function
        if (!refundModal.parentId) {
          throw new Error('Cannot refund to wallet: parent ID not available for this payment');
        }
        if (!refundModal.refundNotes.trim()) {
          throw new Error('A note is required for wallet refunds');
        }
        // payment_id makes the edge function write the refunds ledger row too, so the
        // refund lands in the Refunds tab and the invoice is walked back automatically.
        const { error: walletError } = await supabase.functions.invoke('balance-admin-refund-to-wallet', {
          body: {
            parent_id: refundModal.parentId,
            payment_id: refundModal.paymentId,
            amount_cents: Math.round(refundModal.amount * 100),
            bucket: refundModal.bucket,
            note: refundModal.refundNotes.trim(),
          },
        });
        if (walletError) {
          // functions.invoke surfaces a generic message; the useful one is in the response body.
          const context = (walletError as { context?: Response }).context;
          let detail: string | null = null;
          if (context && typeof context.json === 'function') {
            try {
              const parsed = await context.json() as { message?: string };
              detail = parsed.message ?? null;
            } catch {
              detail = null;
            }
          }
          throw new Error(detail ?? (walletError as { message?: string }).message ?? 'Failed to refund to wallet');
        }
      } else {
        // For non-Stripe payments or manual refunds, create refund record directly
        const { error } = await supabase.from('refunds').insert([
          {
            payment_id: refundModal.paymentId,
            amount: refundModal.amount,
            refund_date: new Date().toISOString(),
            status: 'completed', // Assuming immediate completion for simplicity
            refund_method: refundModal.refundMethod,
            manual_refund_notes: refundModal.refundNotes
          }
        ]);

        if (error) throw error;
      }

      // Close modal and refresh payments
      trackEvent({ eventType: 'admin_refund_process', user: currentUser, userType: 'admin', entityType: 'payment', entityId: refundModal.paymentId, metadata: { ...subjectMetadata({ studentName: refundModal.studentName, programName: refundModal.programName }), amount: refundModal.amount, refund_method: refundModal.refundMethod, payment_method: refundModal.paymentMethod } });
      handleCloseRefundModal();
      fetchPayments();
      fetchInvoices(); // refunds adjust the invoice total/amount_paid
    } catch (error: unknown) {
      console.error('Error processing refund:', error);
      setRefundModal({ ...refundModal, isProcessing: false, error: getErrorMessage(error) });
    }
  }

  async function handleProcessManualPayment() {
    if (!manualPaymentModal.invoiceId) return;

    try {
      setManualPaymentModal({ ...manualPaymentModal, isProcessing: true, error: null });

      // Validate payment amount
      if (manualPaymentModal.paymentAmount <= 0) {
        throw new Error('Payment amount must be greater than zero');
      }

      if (manualPaymentModal.paymentAmount > manualPaymentModal.remainingAmount) {
        throw new Error(`Cannot pay more than the remaining amount: $${manualPaymentModal.remainingAmount.toFixed(2)}`);
      }

      // Create manual payment record
      const { error: paymentError } = await supabase.from('payments').insert([
        {
          invoice_id: manualPaymentModal.invoiceId,
          amount: manualPaymentModal.paymentAmount,
          payment_method: manualPaymentModal.paymentMethod,
          status: 'completed',
          payment_date: manualPaymentModal.paymentDate,
        }
      ]);

      if (paymentError) throw paymentError;

      // Close modal and refresh data
      trackEvent({ eventType: 'admin_manual_payment', user: currentUser, userType: 'admin', entityType: 'invoice', entityId: manualPaymentModal.invoiceId, metadata: { amount: manualPaymentModal.paymentAmount, payment_method: manualPaymentModal.paymentMethod, student_name: manualPaymentModal.studentName } });
      handleCloseManualPaymentModal();
      fetchInvoices();
      fetchPayments(); // Also refresh payments since we added one
    } catch (error: unknown) {
      console.error('Error processing manual payment:', error);
      setManualPaymentModal({ 
        ...manualPaymentModal, 
        isProcessing: false, 
        error: getErrorMessage(error) 
      });
    }
  }

  async function handleOpenInvoiceDetailsModal(invoice: InvoiceData) {
    setInvoiceDetailsModal({
      isOpen: true,
      invoice: invoice,
      detail: null,
      lineItems: [],
      isLoading: true,
      error: null
    });

    try {
      // Fetch the full invoice (enrollment → program, payments) and line items so the
      // admin modal can render the same shared detail body the parent portal shows.
      const [detailRes, lineItemsRes] = await Promise.all([
        supabase
          .from('invoices')
          .select(`
            id,
            enrollment_id,
            total_amount,
            amount_paid,
            status,
            due_date,
            period_start,
            period_end,
            is_prorated,
            created_at,
            updated_at,
            payments (
              id,
              amount,
              payment_method,
              payment_date,
              status,
              bank_account_setup_intent_id,
              bank_account_setup_intents (
                id,
                bank_name,
                account_type,
                last4
              )
            ),
            enrollments!inner (
              id,
              student_id,
              program_id,
              created_at,
              updated_at,
              metadata,
              students!inner (
                first_name,
                last_name,
                date_of_birth
              ),
              programs!inner (
                id,
                name,
                type,
                price,
                billing_type,
                active,
                start_date,
                end_date,
                schedule_cron,
                created_at,
                updated_at
              )
            )
          `)
          .eq('id', invoice.id)
          .maybeSingle(),
        supabase
          .from('invoicing_line_items')
          .select(`
            id,
            description,
            amount,
            line_item_config_id,
            line_item_configs (
              name,
              type,
              subtype
            )
          `)
          .eq('invoice_id', invoice.id)
          .order('created_at', { ascending: true }),
      ]);

      if (detailRes.error) throw detailRes.error;
      if (lineItemsRes.error) throw lineItemsRes.error;

      // Map enrollment metadata.selected_days onto the shape the shared body expects.
      let detail: InvoiceDetailFull | null = null;
      if (detailRes.data) {
        const row = detailRes.data as unknown as { enrollments: { metadata?: Record<string, unknown>; selected_days?: string[] } };
        const enroll = row.enrollments;
        const md = (enroll?.metadata as Record<string, unknown> | undefined) || {};
        enroll.selected_days = Array.isArray(md.selected_days) ? (md.selected_days as string[]) : [];
        detail = row as unknown as InvoiceDetailFull;
      }

      setInvoiceDetailsModal(prev => ({
        ...prev,
        detail,
        lineItems: (lineItemsRes.data as unknown as InvoiceDetailLineItem[]) || [],
        isLoading: false
      }));
    } catch (error: unknown) {
      console.error('Error fetching invoice details:', error);
      setInvoiceDetailsModal(prev => ({
        ...prev,
        isLoading: false,
        error: getErrorMessage(error)
      }));
    }
  }

  function handleCloseInvoiceDetailsModal() {
    setInvoiceDetailsModal({
      isOpen: false,
      invoice: null,
      detail: null,
      lineItems: [],
      isLoading: false,
      error: null,
    });
  }

  async function handleOpenEditInvoiceModal(invoice: InvoiceData) {
    setEditInvoiceModal({
      isOpen: true,
      invoice: invoice,
      lineItems: [],
      enrollmentDetails: null,
      isLoading: true,
      isSaving: false,
      error: null,
      editedStatus: invoice.status,
      editedLineItems: [],
    });

    try {
      // Fetch line items, enrollment details, and the line item config catalog
      // (used for the custom-line-item flow on email-suppressed invoices).
      const [lineItemsResponse, configsResponse, enrollmentResponse] = await Promise.all([
        supabase
          .from('invoicing_line_items')
          .select(`
            id,
            description,
            amount,
            line_item_config_id,
            line_item_configs (
              id,
              name,
              type
            )
          `)
          .eq('invoice_id', invoice.id),

        supabase
          .from('line_item_configs')
          .select('id, name, type, subtype, description')
          .order('type', { ascending: true })
          .order('name', { ascending: true }),

        supabase
          .from('invoices')
          .select(`
            enrollment_id,
            enrollments!inner(
              id,
              enrollment_start_date,
              metadata,
              programs!inner(
                id,
                name,
                type,
                billing_type,
                price
              ),
              students!inner(
                first_name,
                last_name
              )
            )
          `)
          .eq('id', invoice.id)
          .single()
      ]);

      if (lineItemsResponse.error) throw lineItemsResponse.error;
      if (enrollmentResponse.error) throw enrollmentResponse.error;

      const lineItems = lineItemsResponse.data as unknown as InvoiceLineItemData[] || [];
      const enrollmentData = enrollmentResponse.data as unknown as EnrollmentQueryResponse;
      // Exclude system-managed (protected) configs — coupons, processing fees,
      // and refund adjustments are applied by automated flows, not hand-appended.
      const allLineItemConfigs = (configsResponse.error || !configsResponse.data)
        ? []
        : (configsResponse.data as Array<{ id: string; name: string; type: string; subtype: string | null; description: string | null }>)
            .filter(cfg => !isProtectedLineItemConfig(cfg));
      
      // Validate enrollment data structure
      if (!enrollmentData?.enrollments?.programs?.id) {
        console.error('Invalid enrollment data structure:', {
          hasEnrollments: !!enrollmentData?.enrollments,
          hasPrograms: !!enrollmentData?.enrollments?.programs,
          hasProgramId: !!enrollmentData?.enrollments?.programs?.id,
          enrollmentData: enrollmentData
        });
        throw new Error('Invalid enrollment data: missing program information');
      }
      
      // Source of truth: derive selected_days and opt_in_items from metadata
      const md = (enrollmentData.enrollments as unknown as { metadata?: Record<string, unknown> }).metadata || {};
      const mdSelectedDays: string[] = Array.isArray((md as Record<string, unknown>).selected_days)
        ? (md as Record<string, unknown>).selected_days as string[]
        : [];
      const mdOptInItems: string[] = Array.isArray((md as Record<string, unknown>).opt_in_items)
        ? (md as Record<string, unknown>).opt_in_items as string[]
        : [];
      
      // Fetch pricing information to determine which line items are optional
      let lineItemsWithOptionalInfo: InvoiceLineItemData[] = [];
      if (lineItems.length > 0) {
        const lineItemConfigIds = lineItems
          .map(item => item.line_item_configs?.id)
          .filter(Boolean);
          
        if (lineItemConfigIds.length > 0) {
          const { data: pricingData, error: pricingError } = await supabase
            .from('pricing')
            .select('line_item_config_id, is_optional')
            .eq('program_id', enrollmentData.enrollments.programs.id)
            .in('line_item_config_id', lineItemConfigIds);
          
          if (!pricingError && pricingData) {
            lineItemsWithOptionalInfo = lineItems.map(item => ({
              ...item,
              is_optional: pricingData.find(p => p.line_item_config_id === item.line_item_configs?.id)?.is_optional || false
            }));
          } else {
            // If we can't get pricing info, default to not optional
            lineItemsWithOptionalInfo = lineItems.map(item => ({ ...item, is_optional: false }));
          }
        } else {
          lineItemsWithOptionalInfo = lineItems.map(item => ({ ...item, is_optional: false }));
        }
      }
      
      // Fetch the chosen add-ons' details and the program's available optional items
      let optInItemsDetails: Array<{
        id: string;
        name: string;
        type: string;
        amount: number;
      }> = [];
      let availableOptionalItems: Array<{
        id: string;
        name: string;
        type: string;
        description: string;
        amount: number;
      }> = [];
      
      // Log the data we're working with for debugging
      console.log('Debug: Starting to fetch optional items for program:', {
        programId: enrollmentData.enrollments.programs.id,
        optInItems: mdOptInItems,
        hasOptInItems: Array.isArray(mdOptInItems) && mdOptInItems.length > 0
      });
      
      try {
        [optInItemsDetails, availableOptionalItems] = await Promise.all([
          // Fetch the chosen add-ons' details
          mdOptInItems && Array.isArray(mdOptInItems) && mdOptInItems.length > 0
            ? supabase
                .from('pricing')
                .select(`
                  amount,
                  line_item_configs!inner(
                    id,
                    name,
                    type
                  )
                `)
                .eq('program_id', enrollmentData.enrollments.programs.id)
                .in('line_item_config_id', mdOptInItems)
                .then(({ data, error }) => {
                  if (error || !data) return [];
                  const validItems = data.filter((item: PricingQueryItem) => 
                    item.line_item_configs && 
                    item.line_item_configs.length > 0 && 
                    item.line_item_configs[0]
                  );
                  return validItems.map((item: PricingQueryItem) => ({
                    id: item.line_item_configs[0].id,
                    name: item.line_item_configs[0].name,
                    type: item.line_item_configs[0].type,
                    amount: item.amount
                  }));
                })
            : Promise.resolve([]),
          // Fetch all available optional items for this program
          supabase
            .from('pricing')
            .select(`
              amount,
              is_optional,
              line_item_configs!inner(
                id,
                name,
                type,
                description
              )
            `)
            .eq('program_id', enrollmentData.enrollments.programs.id)
            .eq('is_optional', true)
            .then(({ data, error }) => {
              if (error || !data) return [];
              const validItems = data.filter((item: OptionalItemQueryItem) => 
                item.line_item_configs && 
                item.line_item_configs.length > 0 && 
                item.line_item_configs[0]
              );
              return validItems.map((item: OptionalItemQueryItem) => ({
                id: item.line_item_configs[0].id,
                name: item.line_item_configs[0].name,
                type: item.line_item_configs[0].type,
                description: item.line_item_configs[0].description || '',
                amount: item.amount
              }));
            })
        ]);
      } catch (error) {
        console.error('Error fetching optional items:', error);
        // Continue with empty arrays if there's an error
        optInItemsDetails = [];
        availableOptionalItems = [];
      }

      const enrollmentDetails: EnrollmentDetails = {
        id: enrollmentData.enrollments.id,
        enrollment_start_date: enrollmentData.enrollments.enrollment_start_date,
        selected_days: mdSelectedDays,
        opt_in_items: mdOptInItems,
        program: enrollmentData.enrollments.programs,
        student: enrollmentData.enrollments.students,
        optInItemsDetails,
        availableOptionalItems
      };
      
      setEditInvoiceModal(prev => ({
        ...prev,
        lineItems: lineItemsWithOptionalInfo,
        enrollmentDetails: enrollmentDetails,
        allLineItemConfigs,
        editedLineItems: lineItemsWithOptionalInfo.length > 0
          ? lineItemsWithOptionalInfo.map(item => ({
              id: item.id,
              description: item.description || '',
              amount: item.amount,
              isEdited: false,
            }))
          : [],
        isLoading: false
      }));
    } catch (error: unknown) {
      console.error('Error fetching invoice data for edit:', error);
      setEditInvoiceModal(prev => ({
        ...prev,
        isLoading: false,
        error: getErrorMessage(error)
      }));
    }
  }

  function handleCloseEditInvoiceModal() {
    setCustomLineItemConfigId('');
    setEditInvoiceModal({
      isOpen: false,
      invoice: null,
      lineItems: [],
      enrollmentDetails: null,
      isLoading: false,
      isSaving: false,
      error: null,
      editedStatus: '',
      editedLineItems: [],
    });
  }

  // Base a late fee is charged on: the outstanding balance, per the Enrollment &
  // Billing Guide. Any late fee already on the invoice is subtracted so escalating
  // from the first tier to the second does not compound on top of the first fee.
  async function getLateFeeBase(invoiceId: string): Promise<number> {
    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .select('total_amount, amount_paid')
      .eq('id', invoiceId)
      .single();

    if (invoiceError || !invoice) {
      console.error('Error fetching invoice for late fee base:', invoiceError);
      return 0;
    }

    const { data: lateFeeItems, error: lineItemError } = await supabase
      .from('invoicing_line_items')
      .select(`
        amount,
        line_item_configs!inner(name)
      `)
      .eq('invoice_id', invoiceId)
      .eq('line_item_configs.name', 'Late Fee');

    if (lineItemError) {
      console.error('Error fetching existing late fees:', lineItemError);
      return 0;
    }

    const existingLateFees = lateFeeItems?.reduce((sum, item) => sum + item.amount, 0) || 0;
    const outstanding = invoice.total_amount - invoice.amount_paid - existingLateFees;
    return Math.max(outstanding, 0);
  }

  // Handle adding late fee to an invoice
  async function handleAddLateFee(invoice: InvoiceData) {
    setProcessingLateFees(prev => new Set([...prev, invoice.id]));
    
    // Determine the action type based on current state
    const hasFirstLateFee = invoicesWithLateFees.has(invoice.id);
    const hasSecondLateFee = invoicesWithSecondLateFees.has(invoice.id);
    const actionType = hasSecondLateFee ? 'waiving' : (hasFirstLateFee ? 'escalating' : 'adding');
    setLateFeeActions(prev => new Map([...prev, [invoice.id, actionType]]));
    
    // Store the original state and determine if this is an update operation
    const isUpdate = hasFirstLateFee && !hasSecondLateFee;
    
    try {
      // Get the Late Fee line item config
      const { data: lateFeeConfig, error: configError } = await supabase
        .from('line_item_configs')
        .select('id')
        .eq('name', 'Late Fee')
        .single();

      if (configError || !lateFeeConfig) {
        throw new Error('Late Fee configuration not found');
      }

      // Outstanding balance the percentage is charged against
      const lateFeeBase = await getLateFeeBase(invoice.id);

      let description: string;
      let amount: number;

      if (!hasFirstLateFee && !hasSecondLateFee) {
        // First late fee: 10% of the outstanding balance
        description = 'First late fee';
        amount = Math.round(lateFeeBase * LATE_FEE.firstRate * 100) / 100; // Round to 2 decimal places
      } else if (hasFirstLateFee && !hasSecondLateFee) {
        // Update to second late fee: 20% of the outstanding balance
        description = 'Second time late fee';
        amount = Math.round(lateFeeBase * LATE_FEE.secondRate * 100) / 100; // Round to 2 decimal places
      } else {
        throw new Error('Invalid late fee state');
      }

      if (isUpdate) {
        // First find the existing late fee line item
        const { data: existingItem, error: findError } = await supabase
          .from('invoicing_line_items')
          .select('id')
          .eq('invoice_id', invoice.id)
          .eq('line_item_config_id', lateFeeConfig.id)
          .single();

        if (findError || !existingItem) {
          throw new Error('Existing late fee line item not found');
        }

        // Update existing late fee line item
        const { error: updateError } = await supabase
          .from('invoicing_line_items')
          .update({
            description: description,
            amount: amount
          })
          .eq('id', existingItem.id);

        if (updateError) throw updateError;
      } else {
        // Insert new late fee line item
        const { error: insertError } = await supabase
          .from('invoicing_line_items')
          .insert({
            invoice_id: invoice.id,
            line_item_config_id: lateFeeConfig.id,
            description: description,
            amount: amount
          });

        if (insertError) throw insertError;
      }

      // Track the event
      trackEvent({
        eventType: 'admin_late_fee_added',
        user: currentUser,
        userType: 'admin',
        entityType: 'invoice',
        entityId: invoice.id,
        metadata: {
          ...invoiceSubject(invoice),
          late_fee_amount: amount,
          late_fee_description: description,
          previous_total: invoice.total_amount,
          admin_action: true
        },
        success: true
      });

      // Update the local state based on the action
      if (isUpdate) {
        // Move from first to second late fee
        setInvoicesWithLateFees(prev => {
          const newSet = new Set(prev);
          newSet.delete(invoice.id);
          return newSet;
        });
        setInvoicesWithSecondLateFees(prev => new Set([...prev, invoice.id]));
      } else {
        // Add first late fee
        setInvoicesWithLateFees(prev => new Set([...prev, invoice.id]));
      }

      // Refresh the specific invoice data in place
      await refreshInvoiceData(invoice.id);
    } catch (error: unknown) {
      console.error('Error adding late fee:', error);
      // Revert the local state update on error based on what was being attempted
      if (isUpdate) {
        // If we were escalating (first to second), revert back to first late fee state
        setInvoicesWithLateFees(prev => new Set([...prev, invoice.id]));
        setInvoicesWithSecondLateFees(prev => {
          const newSet = new Set(prev);
          newSet.delete(invoice.id);
          return newSet;
        });
      } else {
        // If we were adding first late fee, remove it
        setInvoicesWithLateFees(prev => {
          const newSet = new Set(prev);
          newSet.delete(invoice.id);
          return newSet;
        });
      }
    } finally {
      setProcessingLateFees(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id);
        return newSet;
      });
      setLateFeeActions(prev => {
        const newMap = new Map(prev);
        newMap.delete(invoice.id);
        return newMap;
      });
    }
  }

  // Handle removing late fee from an invoice
  async function handleRemoveLateFee(invoice: InvoiceData) {
    setProcessingLateFees(prev => new Set([...prev, invoice.id]));
    setLateFeeActions(prev => new Map([...prev, [invoice.id, 'waiving']]));
    
    // Store the original state before attempting removal
    const hadFirstLateFee = invoicesWithLateFees.has(invoice.id);
    const hadSecondLateFee = invoicesWithSecondLateFees.has(invoice.id);
    
    try {
      // Get the late fee line item
      const { data: lateFeeItem, error: itemError } = await supabase
        .from('invoicing_line_items')
        .select(`
          id, 
          amount,
          description,
          line_item_configs!inner(name)
        `)
        .eq('invoice_id', invoice.id)
        .eq('line_item_configs.name', 'Late Fee')
        .single();

      if (itemError || !lateFeeItem) {
        throw new Error('Late fee not found on this invoice');
      }

      // Remove the late fee line item
      const { error: deleteError } = await supabase
        .from('invoicing_line_items')
        .delete()
        .eq('id', lateFeeItem.id);

      if (deleteError) throw deleteError;

      // Track the event
      trackEvent({
        eventType: 'admin_late_fee_waived',
        user: currentUser,
        userType: 'admin',
        entityType: 'invoice',
        entityId: invoice.id,
        metadata: {
          ...invoiceSubject(invoice),
          late_fee_amount: lateFeeItem.amount,
          late_fee_description: lateFeeItem.description,
          previous_total: invoice.total_amount,
          admin_action: true
        },
        success: true
      });

      // Update the local state to reflect the late fee removal
      setInvoicesWithLateFees(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id);
        return newSet;
      });
      setInvoicesWithSecondLateFees(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id);
        return newSet;
      });

      // Refresh the specific invoice data in place
      await refreshInvoiceData(invoice.id);
    } catch (error: unknown) {
      console.error('Error removing late fee:', error);
      // Revert the local state update on error based on what was originally there
      if (hadSecondLateFee) {
        // Restore second late fee state
        setInvoicesWithSecondLateFees(prev => new Set([...prev, invoice.id]));
        // Ensure it's not in the first late fee state
        setInvoicesWithLateFees(prev => {
          const newSet = new Set(prev);
          newSet.delete(invoice.id);
          return newSet;
        });
      } else if (hadFirstLateFee) {
        // Restore first late fee state
        setInvoicesWithLateFees(prev => new Set([...prev, invoice.id]));
        setInvoicesWithSecondLateFees(prev => {
          const newSet = new Set(prev);
          newSet.delete(invoice.id);
          return newSet;
        });
      }
    } finally {
      setProcessingLateFees(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id);
        return newSet;
      });
      setLateFeeActions(prev => {
        const newMap = new Map(prev);
        newMap.delete(invoice.id);
        return newMap;
      });
    }
  }

  function handleAddOptionalLineItem(optionalItem: {id: string; name: string; amount: number; type: string; description: string}) {
    if (!editInvoiceModal.invoice) return;

    // Calculate the amount based on billing type and enrollment details
    let calculatedAmount = optionalItem.amount;
    if (editInvoiceModal.enrollmentDetails) {
      const { program, selected_days } = editInvoiceModal.enrollmentDetails;
      
      if (program.billing_type === 'per_day' && selected_days && selected_days.length > 0) {
        // For per-day billing, multiply by number of selected days (unless it's materials)
        if (optionalItem.type !== 'materials') {
          calculatedAmount = optionalItem.amount * selected_days.length;
        }
      }
      // Note: For recurring_monthly, we'd need the start date to calculate proration
      // but for simplicity, we'll use the base amount and let admin adjust if needed
    }

    // Create a temporary ID for the new item
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const newLineItem = {
      id: tempId,
      description: optionalItem.description || optionalItem.name,
      amount: calculatedAmount,
      isEdited: true, // Mark as edited so it gets saved
      lineItemConfigId: optionalItem.id
    };

    setEditInvoiceModal(prev => ({
      ...prev,
      editedLineItems: [...prev.editedLineItems, newLineItem]
    }));
  }

  // Append a custom line item built from any line item config. Only used on
  // email-suppressed invoices (gated by canFreelyEditInvoice in the UI). The
  // admin can then customize the amount and description inline before saving.
  function handleAddCustomLineItem(config: { id: string; name: string; description: string | null }) {
    if (!editInvoiceModal.invoice) return;

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const newLineItem = {
      id: tempId,
      description: config.description || config.name,
      amount: 0,
      isEdited: true,
      lineItemConfigId: config.id
    };

    setEditInvoiceModal(prev => {
      // Guard against applying the same config twice on one invoice.
      const alreadyPresent = prev.editedLineItems.some(ei => {
        if (ei.id.startsWith('temp_')) return ei.lineItemConfigId === config.id;
        const original = prev.lineItems.find(li => li.id === ei.id);
        return original?.line_item_configs?.id === config.id;
      });
      if (alreadyPresent) return prev;

      return {
        ...prev,
        editedLineItems: [...prev.editedLineItems, newLineItem]
      };
    });
  }

  async function handleSaveInvoiceEdit() {
    if (!editInvoiceModal.invoice) return;

    setEditInvoiceModal(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      // Update invoice status if changed
      if (editInvoiceModal.editedStatus !== editInvoiceModal.invoice.status) {
        const { error: invoiceError } = await supabase
          .from('invoices')
          .update({ 
            status: editInvoiceModal.editedStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', editInvoiceModal.invoice.id);

        if (invoiceError) throw invoiceError;
      }

      // Handle removed line items (items that were in original but not in current edited list)
      const removedItemIds = editInvoiceModal.lineItems
        .filter(originalItem => !editInvoiceModal.editedLineItems.some(editedItem => editedItem.id === originalItem.id))
        .map(item => item.id);
      
      for (const removedId of removedItemIds) {
        const { error: deleteError } = await supabase
          .from('invoicing_line_items')
          .delete()
          .eq('id', removedId);
        
        if (deleteError) throw deleteError;
      }

      // Update existing line items and insert new ones
      const editedItems = editInvoiceModal.editedLineItems.filter(item => item.isEdited);
      
      for (const item of editedItems) {
        if (item.id.startsWith('temp_')) {
          // This is a new line item, insert it. Prefer the config id captured
          // when the item was added; fall back to matching an available optional
          // item by description for older code paths.
          const resolvedConfigId = item.lineItemConfigId
            ?? editInvoiceModal.enrollmentDetails?.availableOptionalItems?.find(
              opt => opt.name === item.description || opt.description === item.description
            )?.id;

          if (resolvedConfigId) {
            const { error: insertError } = await supabase
              .from('invoicing_line_items')
              .insert({
                invoice_id: editInvoiceModal.invoice.id,
                line_item_config_id: resolvedConfigId,
                description: item.description,
                amount: item.amount,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });

            if (insertError) throw insertError;
          }
        } else {
          // This is an existing line item, update it
          const { error: updateError } = await supabase
            .from('invoicing_line_items')
            .update({ 
              description: item.description,
              amount: item.amount,
              updated_at: new Date().toISOString()
            })
            .eq('id', item.id);

          if (updateError) throw updateError;
        }
      }

      // Refresh the invoices data
      await fetchInvoices();
      
      trackEvent({
        eventType: 'admin_invoice_edit',
        user: currentUser,
        userType: 'admin',
        entityType: 'invoice',
        entityId: editInvoiceModal.invoice.id,
        metadata: {
          ...invoiceSubject(editInvoiceModal.invoice),
          status_changed: editInvoiceModal.editedStatus !== editInvoiceModal.invoice.status,
          line_items_changed: editedItems.length > 0 || removedItemIds.length > 0,
          edited_line_items_count: editedItems.length,
          removed_line_items_count: removedItemIds.length,
          new_status: editInvoiceModal.editedStatus,
          old_status: editInvoiceModal.invoice.status,
          admin_action: true
        },
        success: true
      });
      
      // Close modal
      handleCloseEditInvoiceModal();
      
    } catch (error: unknown) {
      console.error('Error saving invoice changes:', error);
      const errorMessage = getErrorMessage(error);
      
      trackEvent({
        eventType: 'admin_invoice_edit',
        user: currentUser,
        userType: 'admin',
        entityType: 'invoice',
        entityId: editInvoiceModal.invoice?.id,
        metadata: {
          ...(editInvoiceModal.invoice ? invoiceSubject(editInvoiceModal.invoice) : {}),
          attempted_status_change: editInvoiceModal.editedStatus !== editInvoiceModal.invoice?.status,
          attempted_line_items_change: editInvoiceModal.editedLineItems.filter(item => item.isEdited).length > 0,
          admin_action: true
        },
        success: false,
        errorMessage: errorMessage
      });
      
      setEditInvoiceModal(prev => ({
        ...prev,
        isSaving: false,
        error: errorMessage
      }));
    }
  }

  // All payments filtering and sorting is handled server-side.

  // All invoices filtering and sorting is handled server-side.

  // Apply filters and sorting to the refunds data
  const filteredRefunds = refundsData.filter(refund => {
    // Enhanced search functionality
    const searchMatch = (() => {
      if (!refundFilters.search.trim()) return true;
      
      const keywords = refundFilters.search.toLowerCase().trim().split(/\s+/);
      const searchableFields = [
        refund.student_name?.toLowerCase() || '',
        refund.program_name?.toLowerCase() || '',
        refund.refund_method?.toLowerCase() || '',
        refund.status?.toLowerCase() || '',
        refund.paid_by_parent_name?.toLowerCase() || '',
        refund.paid_by_parent_email?.toLowerCase() || '',
        refund.period_start?.toLowerCase() || '',
        refund.period_end?.toLowerCase() || ''
      ];
      
      // For single keyword, check if any field contains it
      if (keywords.length === 1) {
        return searchableFields.some(field => field.includes(keywords[0]));
      }
      
      // For multiple keywords, each keyword must match a different field
      const matchedFields = new Set<number>();
      
      for (const keyword of keywords) {
        let keywordMatched = false;
        for (let i = 0; i < searchableFields.length; i++) {
          if (!matchedFields.has(i) && searchableFields[i].includes(keyword)) {
            matchedFields.add(i);
            keywordMatched = true;
            break;
          }
        }
        if (!keywordMatched) {
          return false; // If any keyword doesn't match, return false
        }
      }
      
      return true; // All keywords matched different fields
    })();

    // Apply date filters with consistent timezone handling
    let dateMatch = true;
    if (refundFilters.startDate) {
      // Parse both dates consistently to avoid timezone issues
      const refundDate = new Date(refund.created_at);
      const filterStartDate = new Date(refundFilters.startDate + 'T00:00:00');
      dateMatch = dateMatch && refundDate >= filterStartDate;
    }
    if (refundFilters.endDate) {
      const refundDate = new Date(refund.created_at);
      const filterEndDate = new Date(refundFilters.endDate + 'T23:59:59');
      dateMatch = dateMatch && refundDate <= filterEndDate;
    }

    // Apply status filter
    const statusMatch = refundFilters.status === 'all' || refund.status === refundFilters.status;

    return searchMatch && dateMatch && statusMatch && matchesBrandFilter(refund.brand_key, brandFilter);
  }).sort((a, b) => {
    // Apply sorting
    if (refundSort.column === 'amount') {
      return refundSort.direction === 'asc' ? a.amount - b.amount : b.amount - a.amount;
    } else if (refundSort.column === 'refund_date') {
      return refundSort.direction === 'asc' 
        ? new Date(a.refund_date).getTime() - new Date(b.refund_date).getTime()
        : new Date(b.refund_date).getTime() - new Date(a.refund_date).getTime();
    } else if (refundSort.column === 'created_at') {
      return refundSort.direction === 'asc' 
        ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    } else {
      // Sort by string columns (student_name, program_name)
      const aValue = a[refundSort.column];
      const bValue = b[refundSort.column];
      return refundSort.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
  });

  // Re-run whichever tab's fetch failed. Mirrors the per-tab effects above.
  function retryActiveTab() {
    if (activeTab === 'payments') fetchPayments();
    else if (activeTab === 'invoices') fetchInvoices();
    else if (activeTab === 'refunds') fetchRefunds();
    else if (activeTab === 'debt') fetchDebtInvoices();
  }

  return (
    <AdminLayout>
      {/* A failed fetch on one tab used to replace the whole Finances page,
          leaving no way back except a browser reload. Show it as a banner
          instead so the tabs, filters and any already-loaded data survive. */}
      {error && (
        <div
          role="alert"
          className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded flex items-start justify-between gap-4"
        >
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={retryActiveTab}
              className="text-sm font-medium px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
              className="text-sm font-medium px-2 py-1 rounded text-red-700 hover:bg-red-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
        <div className="border-b border-gray-200 flex-shrink-0">
          <nav className="-mb-px flex flex-wrap items-center justify-between sm:flex-nowrap">
            <div className="flex">
              <button
                className={`whitespace-nowrap py-4 px-3 sm:px-6 text-sm font-medium ${
                  activeTab === 'invoices'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-current={activeTab === 'invoices' ? 'page' : undefined}
                onClick={() => setActiveTab('invoices')}
              >
                Invoices
              </button>
              <button
                className={`whitespace-nowrap py-4 px-3 sm:px-6 text-sm font-medium ${
                  activeTab === 'payments'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-current={activeTab === 'payments' ? 'page' : undefined}
                onClick={() => setActiveTab('payments')}
              >
                Payments
              </button>
              <button
                className={`whitespace-nowrap py-4 px-3 sm:px-6 text-sm font-medium ${
                  activeTab === 'carts'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-current={activeTab === 'carts' ? 'page' : undefined}
                onClick={() => setActiveTab('carts')}
              >
                Bundles
              </button>
              <button
                className={`whitespace-nowrap py-4 px-3 sm:px-6 text-sm font-medium ${
                  activeTab === 'refunds'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-current={activeTab === 'refunds' ? 'page' : undefined}
                onClick={() => setActiveTab('refunds')}
              >
                Refunds
              </button>
              <button
                className={`whitespace-nowrap py-4 px-3 sm:px-6 text-sm font-medium ${
                  activeTab === 'debt'
                    ? 'border-b-2 border-red-500 text-red-600'
                    : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-current={activeTab === 'debt' ? 'page' : undefined}
                onClick={() => setActiveTab('debt')}
              >
                <span className="hidden sm:inline">Debt ({debtDaysDisplay}+ days)</span>
                <span className="sm:hidden">Debt</span>
              </button>
            </div>
            <div className="flex items-center gap-2 pr-2 sm:pr-4 py-2 sm:py-0">
              {/* Mobile compact CTA */}
              <button
                type="button"
                className="inline-flex sm:hidden items-center px-2 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                onClick={() => setShowBatchModal(true)}
                aria-label="Run monthly invoices"
              >
                Invoices
              </button>
              <button
                type="button"
                className="inline-flex sm:hidden items-center px-2 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
                onClick={() => setShowAutopayModal(true)}
                aria-label="Run autopay"
              >
                Autopay
              </button>
              {/* Desktop CTA */}
              <button
                type="button"
                className="hidden sm:inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                onClick={() => setShowBatchModal(true)}
              >
                Run monthly invoices
              </button>
              <button
                type="button"
                className="hidden sm:inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                onClick={() => setShowAutopayModal(true)}
              >
                Run autopay
              </button>
              <FlexSeedButton />
            </div>
          </nav>
        </div>

        {activeTab === 'payments' && (
          <div className="lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
            {/* Filters */}
            <div className="px-4 py-2 border-b border-gray-200 flex-shrink-0 space-y-2">
              <DateRangeFilter
                idPrefix="payment"
                value={paymentFilters}
                onChange={(range) => {
                  releaseFocusedPayment();
                  setPaymentsPage(1);
                  setPaymentFilters(prev => ({ ...prev, ...range }));
                }}
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div>
                  <label htmlFor="payment-search" className="block text-xs font-medium text-gray-600">Search</label>
                  <input type="text" id="payment-search" className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={paymentFilters.search} onChange={(e) => setPaymentFilters({ ...paymentFilters, search: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') { releaseFocusedPayment(); setPaymentsPage(1); setAppliedPaymentSearch(paymentFilters.search.trim()); } }} placeholder="Student or program" />
                </div>
                <div>
                  <label htmlFor="payment-status" className="block text-xs font-medium text-gray-600">Status</label>
                  <select id="payment-status" className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={paymentFilters.status} onChange={(e) => { releaseFocusedPayment(); setPaymentFilters({ ...paymentFilters, status: e.target.value }); }}>
                    <option value="all">All</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="created">Created (ACH)</option>
                    <option value="processing">Processing (ACH)</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                    <option value="partially_refunded">Partially Refunded</option>
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label htmlFor="payment-brand" className="block text-xs font-medium text-gray-600">Brand</label>
                  <BrandFilterSelect id="payment-brand" value={brandFilter} onChange={setBrandFilter} className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                </div>
              </div>
            </div>

            {focusedPaymentId && (
              <FocusedRecordBanner
                noun="payment"
                loading={paymentsLoading}
                found={paymentsData.length > 0}
                onShowAll={clearFocusedPayment}
              />
            )}

            {/* Payments Top Pagination */}
            <div className="p-4 pb-2 flex flex-col sm:flex-row items-center justify-between gap-3 flex-shrink-0">
              {(() => {
                const totalPages = Math.max(1, Math.ceil(paymentsTotal / PAGE_SIZE));
                const start = (paymentsPage - 1) * PAGE_SIZE + 1;
                const end = Math.min(paymentsPage * PAGE_SIZE, paymentsTotal);
                return (
                  <>
                    <div className="text-sm text-gray-600 flex items-center gap-1.5">
                      {paymentsLoading ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                          Loading payments...
                        </>
                      ) : (
                        `Showing ${paymentsTotal === 0 ? 0 : start} to ${end} of ${paymentsTotal} payments`
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setPaymentsPage(1)}
                        disabled={paymentsPage === 1}
                      >
                        First
                      </button>
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setPaymentsPage(Math.max(1, paymentsPage - 1))}
                        disabled={paymentsPage === 1}
                      >
                        Prev
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {paymentsPage} of {totalPages}
                      </span>
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setPaymentsPage(Math.min(totalPages, paymentsPage + 1))}
                        disabled={paymentsPage === totalPages}
                      >
                        Next
                      </button>
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setPaymentsPage(totalPages)}
                        disabled={paymentsPage === totalPages}
                      >
                        Last
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Payments Table */}
            <div className={`overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0 transition-opacity${paymentsLoading ? ' opacity-60' : ''}`}>
              <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => {
                        setPaymentsPage(1);
                        setPaymentSort({
                          column: 'student_name',
                          direction: paymentSort.column === 'student_name' && paymentSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Student
                      {paymentSort.column === 'student_name' && (
                        <span className="ml-1">{paymentSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => {
                        setPaymentsPage(1);
                        setPaymentSort({
                          column: 'program_name',
                          direction: paymentSort.column === 'program_name' && paymentSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Program
                      {paymentSort.column === 'program_name' && (
                        <span className="ml-1">{paymentSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Period
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Paid By
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Method
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => {
                        setPaymentsPage(1);
                        setPaymentSort({
                          column: 'amount',
                          direction: paymentSort.column === 'amount' && paymentSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Amount
                      {paymentSort.column === 'amount' && (
                        <span className="ml-1">{paymentSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => {
                        setPaymentsPage(1);
                        setPaymentSort({
                          column: 'created_at',
                          direction: paymentSort.column === 'created_at' && paymentSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Date
                      {paymentSort.column === 'created_at' && (
                        <span className="ml-1">{paymentSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="relative px-6 py-3"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paymentsData.map((payment) => (
                    <React.Fragment key={payment.id}>
                      <tr className={selectedPaymentId === payment.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} onClick={() => setSelectedPaymentId(selectedPaymentId === payment.id ? null : payment.id)}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {payment.student_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {payment.program_name} <AdminBrandTag brand={payment.brand_key} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatPeriodRange(payment.period_start, payment.period_end)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {payment.paid_by_parent_name || payment.paid_by_parent_email || (payment.paid_by_parent_id ? `Parent ${payment.paid_by_parent_id.slice(0, 8)}…` : '—')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {payment.payment_method.charAt(0).toUpperCase() + payment.payment_method.slice(1)}
                          {payment.cart_id && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setOpenCartId(payment.cart_id ?? null); }}
                              className="ml-1 inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                              title="One payment for several invoices"
                              data-testid={`payment-cart-${payment.id}`}
                            >
                              Bundle
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${payment.amount.toFixed(2)}
                          {payment.refunded_amount ? (
                            <span className="text-xs text-yellow-600 block">
                              (${payment.refunded_amount.toFixed(2)} refunded)
                            </span>
                          ) : null}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              payment.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : payment.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : payment.status === 'created' || payment.status === 'processing'
                                ? 'bg-blue-100 text-blue-800'
                                : payment.status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : payment.status === 'refunded'
                                ? 'bg-red-100 text-red-800'
                                : payment.status === 'partially_refunded'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {payment.status.charAt(0).toUpperCase() + payment.status.slice(1).replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDateInBusinessTZ(payment.created_at)}</div>
                          <div className="text-xs text-gray-400">{formatTimeInBusinessTZ(payment.created_at)}</div>
                        </td>
                      </tr>
                      {selectedPaymentId === payment.id && (
                        <tr key={`${payment.id}-actions`} className="bg-blue-50">
                          <td colSpan={8} className="px-6 py-2 border-t border-blue-100">
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); copyPaymentId(payment.id); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                title="Copy payment ID to clipboard"
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Copy ID
                              </button>
                              {['completed', 'partially_refunded'].includes(payment.status) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleOpenRefundModal(payment); }}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                                >
                                  <CreditCard className="h-3 w-3 mr-1" />
                                  Refund
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {!paymentsLoading && paymentsData.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                        No payments found matching the filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Mobile Card View for Payments */}
              <div className="lg:hidden">
                {paymentsData.map((payment) => (
                  <div key={payment.id} className={`border-b border-gray-200 p-3 cursor-pointer ${selectedPaymentId === payment.id ? 'bg-blue-50' : ''}`} onClick={() => setSelectedPaymentId(selectedPaymentId === payment.id ? null : payment.id)}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="text-base font-bold text-gray-900">{payment.student_name}</div>
                        <div className="text-sm text-gray-600">{payment.program_name} <AdminBrandTag brand={payment.brand_key} /></div>
                        <div className="text-xs text-gray-500">{formatPeriodRange(payment.period_start, payment.period_end)}</div>
                        <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                          payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                          payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          payment.status === 'created' || payment.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                          payment.status === 'refunded' ? 'bg-red-100 text-red-800' :
                          payment.status === 'partially_refunded' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {payment.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">${payment.amount.toFixed(2)}</div>
                        {payment.refunded_amount ? (
                          <div className="text-xs text-yellow-600">(${payment.refunded_amount.toFixed(2)} refunded)</div>
                        ) : null}
                        <div className="text-xs text-gray-500">
                          {payment.payment_method}
                          {payment.cart_id && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setOpenCartId(payment.cart_id ?? null); }}
                              className="ml-1 inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                              title="One payment for several invoices"
                              data-testid={`payment-cart-${payment.id}`}
                            >
                              Bundle
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {payment.paid_by_parent_name || payment.paid_by_parent_email || (payment.paid_by_parent_id ? `Parent ${payment.paid_by_parent_id.slice(0, 8)}…` : '—')}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">{formatDateInBusinessTZ(payment.created_at)}</div>
                        <div className="text-xs text-gray-400">{formatTimeInBusinessTZ(payment.created_at)}</div>
                      </div>
                    </div>
                    {selectedPaymentId === payment.id && (
                      <div className="mt-2 pt-2 border-t border-blue-100 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => copyPaymentId(payment.id)} className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors" title="Copy payment ID">
                          <Copy className="h-3 w-3 mr-1" />Copy ID
                        </button>
                        {['completed', 'partially_refunded'].includes(payment.status) && (
                          <button onClick={() => handleOpenRefundModal(payment)} className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors">
                            <CreditCard className="h-3 w-3 mr-1" />Refund
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {!paymentsLoading && paymentsData.length === 0 && (
                  <div className="text-center py-6 text-sm text-gray-500">
                    No payments found.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {activeTab === 'invoices' && (
          <div className="lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
            {/* Email Toast Notification — invoice email and late fee notice sends */}
            {reminderToast && (
              <div className={`p-3 flex-shrink-0 ${reminderToast.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border-b`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{reminderToast.type === 'success' ? '✅' : '❌'}</span>
                    <span className={`text-sm ${reminderToast.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                      {reminderToast.message}
                    </span>
                  </div>
                  <button
                    onClick={() => setReminderToast(null)}
                    className={`text-sm ${reminderToast.type === 'success' ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'}`}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="px-4 py-2 border-b border-gray-200 flex-shrink-0 space-y-2">
              <DateRangeFilter
                idPrefix="invoice"
                value={invoiceFilters}
                onChange={(range) => {
                  releaseFocusedInvoice();
                  setInvoicesPage(1);
                  setInvoiceFilters(prev => ({ ...prev, ...range }));
                }}
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div>
                  <label htmlFor="invoice-search" className="block text-xs font-medium text-gray-600">Search</label>
                  <input type="text" id="invoice-search" className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={invoiceFilters.search} onChange={(e) => setInvoiceFilters({ ...invoiceFilters, search: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') { releaseFocusedInvoice(); setInvoicesPage(1); setAppliedInvoiceSearch(invoiceFilters.search.trim()); } }} placeholder="Student or program" />
                </div>
                <div>
                  <label htmlFor="invoice-status" className="block text-xs font-medium text-gray-600">Status</label>
                  <select id="invoice-status" className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={invoiceFilters.status} onChange={(e) => { releaseFocusedInvoice(); setInvoiceFilters({ ...invoiceFilters, status: e.target.value }); }}>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="payment_processing">Payment Processing</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="voided">Voided</option>
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label htmlFor="invoice-brand" className="block text-xs font-medium text-gray-600">Brand</label>
                  <BrandFilterSelect id="invoice-brand" value={brandFilter} onChange={setBrandFilter} className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                </div>
              </div>
            </div>

            {focusedInvoiceId && (
              <FocusedRecordBanner
                noun="invoice"
                loading={invoicesLoading}
                found={invoicesData.length > 0}
                onShowAll={clearFocusedInvoice}
              />
            )}

            {/* Invoices Top Pagination */}
            <div className="p-4 pb-2 flex flex-col sm:flex-row items-center justify-between gap-3 flex-shrink-0">
              {(() => {
                const totalPages = Math.max(1, Math.ceil(invoicesTotal / PAGE_SIZE));
                const start = (invoicesPage - 1) * PAGE_SIZE + 1;
                const end = Math.min(invoicesPage * PAGE_SIZE, invoicesTotal);
                return (
                  <>
                    <div className="text-sm text-gray-600 flex items-center gap-1.5">
                      {invoicesLoading ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                          Loading invoices...
                        </>
                      ) : (
                        `Showing ${invoicesTotal === 0 ? 0 : start} to ${end} of ${invoicesTotal} invoices`
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setInvoicesPage(1)}
                        disabled={invoicesPage === 1}
                      >
                        First
                      </button>
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setInvoicesPage(Math.max(1, invoicesPage - 1))}
                        disabled={invoicesPage === 1}
                      >
                        Prev
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {invoicesPage} of {totalPages}
                      </span>
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setInvoicesPage(Math.min(totalPages, invoicesPage + 1))}
                        disabled={invoicesPage === totalPages}
                      >
                        Next
                      </button>
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setInvoicesPage(totalPages)}
                        disabled={invoicesPage === totalPages}
                      >
                        Last
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Invoices Table */}
            <div className={`overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0 transition-opacity${invoicesLoading ? ' opacity-60' : ''}`}>
              <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-50"
                      onClick={() => {
                        setInvoicesPage(1);
                        setInvoiceSort({
                          column: 'student_name',
                          direction: invoiceSort.column === 'student_name' && invoiceSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Student
                      {invoiceSort.column === 'student_name' && (
                        <span className="ml-1">{invoiceSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-50"
                      onClick={() => {
                        setInvoicesPage(1);
                        setInvoiceSort({
                          column: 'program_name',
                          direction: invoiceSort.column === 'program_name' && invoiceSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Program
                      {invoiceSort.column === 'program_name' && (
                        <span className="ml-1">{invoiceSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                    >
                      Enrolled By
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                    >
                      Memo Code
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                    >
                      Period
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                    >
                      Due Date
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-50"
                      onClick={() => {
                        setInvoicesPage(1);
                        setInvoiceSort({
                          column: 'created_at',
                          direction: invoiceSort.column === 'created_at' && invoiceSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Created
                      {invoiceSort.column === 'created_at' && (
                        <span className="ml-1">{invoiceSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-50"
                      onClick={() => {
                        setInvoicesPage(1);
                        setInvoiceSort({
                          column: 'updated_at',
                          direction: invoiceSort.column === 'updated_at' && invoiceSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Updated
                      {invoiceSort.column === 'updated_at' && (
                        <span className="ml-1">{invoiceSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoicesData.map((invoice) => (
                    <React.Fragment key={invoice.id}>
                      <tr className={selectedInvoiceId === invoice.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} onClick={() => setSelectedInvoiceId(selectedInvoiceId === invoice.id ? null : invoice.id)}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.student_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.program_name} <AdminBrandTag brand={invoice.brand_key} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.enrolled_by_parent_name || invoice.enrolled_by_parent_email || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            ${invoice.total_amount.toFixed(2)} total
                          </div>
                          <div className="text-xs text-gray-500">
                            ${invoice.amount_paid.toFixed(2)} paid
                          </div>
                          <div className="text-xs text-gray-500">
                            ${(invoice.total_amount - invoice.amount_paid).toFixed(2)} remaining
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.memo_code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              invoice.status === 'paid'
                                ? 'bg-green-100 text-green-800'
                                : invoice.status === 'partial'
                                ? 'bg-yellow-100 text-yellow-800'
                                : invoice.status === 'overdue'
                                ? 'bg-red-100 text-red-800'
                                : invoice.status === 'voided'
                                ? 'bg-gray-300 text-gray-700'
                                : invoice.status === 'payment_processing'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1).replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatPeriodRange(invoice.period_start, invoice.period_end)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateDisplay(invoice.due_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDateInBusinessTZ(invoice.created_at)}</div>
                          <div className="text-xs text-gray-400">{formatTimeInBusinessTZ(invoice.created_at)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDateInBusinessTZ(invoice.updated_at)}</div>
                          <div className="text-xs text-gray-400">{formatTimeInBusinessTZ(invoice.updated_at)}</div>
                        </td>
                      </tr>
                      {selectedInvoiceId === invoice.id && (
                        <tr key={`${invoice.id}-actions`} className="bg-blue-50">
                          <td colSpan={10} className="px-6 py-2 border-t border-blue-100">
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); copyInvoiceId(invoice.id); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                title="Copy invoice ID to clipboard"
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Copy ID
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleOpenInvoiceDetailsModal(invoice); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                View Details
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleOpenEditInvoiceModal(invoice); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Edit
                              </button>
                              {invoice.email_suppressed && invoice.status !== 'voided' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSendInvoiceEmail(invoice); }}
                                  disabled={sendingInvoiceEmail === invoice.id}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-300 rounded-md hover:bg-amber-100 hover:text-amber-900 transition-colors disabled:opacity-50"
                                  title="Email was held on create — send invoice notification now"
                                >
                                  <Mail className="h-3 w-3 mr-1" />
                                  {sendingInvoiceEmail === invoice.id ? 'Sending…' : 'Send Email'}
                                </button>
                              )}
                              {['pending', 'partial', 'overdue'].includes(invoice.status) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleOpenManualPaymentModal(invoice); }}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                                >
                                  <CreditCard className="h-3 w-3 mr-1" />
                                  Mark as Paid
                                </button>
                              )}
                              {['pending', 'partial', 'overdue'].includes(invoice.status) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleCompInvoice(invoice); }}
                                  disabled={compingInvoice === invoice.id}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 hover:text-purple-900 transition-colors disabled:opacity-50"
                                  title="Comp to $0 — adds a no-charge discount and marks paid, keeping the original line items"
                                >
                                  <Gift className="h-3 w-3 mr-1" />
                                  {compingInvoice === invoice.id ? 'Comping…' : 'Comp'}
                                </button>
                              )}
                              {invoice.status === 'pending' && isInvoiceLate(invoice.due_date) && (
                                (() => {
                                  const hasFirstLateFee = invoicesWithLateFees.has(invoice.id);
                                  const hasSecondLateFee = invoicesWithSecondLateFees.has(invoice.id);
                                  const isProcessing = processingLateFees.has(invoice.id);
                                  if (hasSecondLateFee) {
                                    return (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleRemoveLateFee(invoice); }}
                                        disabled={isProcessing}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                                        title="Waive late fee"
                                      >
                                        <DollarSign className="h-3 w-3 mr-1" />
                                        Waive Late Fee
                                      </button>
                                    );
                                  } else if (hasFirstLateFee) {
                                    return (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleAddLateFee(invoice); }}
                                        disabled={isProcessing}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 transition-colors"
                                        title={`Escalate to ${formatLateFeeRate(LATE_FEE.secondRate)} late fee`}
                                      >
                                        <Clock className="h-3 w-3 mr-1" />
                                        More Late Fee
                                      </button>
                                    );
                                  } else {
                                    return (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleAddLateFee(invoice); }}
                                        disabled={isProcessing}
                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                                        title={`Add ${formatLateFeeRate(LATE_FEE.firstRate)} late fee`}
                                      >
                                        <Clock className="h-3 w-3 mr-1" />
                                        Late Fee
                                      </button>
                                    );
                                  }
                                })()
                              )}
                              {(invoicesWithLateFees.has(invoice.id) || invoicesWithSecondLateFees.has(invoice.id)) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSendLateFeeNotice(invoice); }}
                                  disabled={sendingLateFeeNotice === invoice.id || !invoice.enrolled_by_parent_email}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={invoice.enrolled_by_parent_email ? `Email the late fee notice to ${invoice.enrolled_by_parent_email}` : 'No parent email available'}
                                >
                                  <Mail className="h-3 w-3 mr-1" />
                                  {sendingLateFeeNotice === invoice.id ? 'Sending...' : 'Late Fee Notice'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {!invoicesLoading && invoicesData.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-6 py-4 text-center text-sm text-gray-500">
                        No invoices found matching the filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Mobile Card View for Invoices */}
              <div className="lg:hidden">
                {invoicesData.map((invoice) => (
                  <div key={invoice.id} className={`border-b border-gray-200 p-3 cursor-pointer ${selectedInvoiceId === invoice.id ? 'bg-blue-50' : ''}`} onClick={() => setSelectedInvoiceId(selectedInvoiceId === invoice.id ? null : invoice.id)}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="text-base font-bold text-gray-900">{invoice.student_name}</div>
                        <div className="text-sm text-gray-600">{invoice.program_name} <AdminBrandTag brand={invoice.brand_key} /></div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">${invoice.total_amount.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">${invoice.amount_paid.toFixed(2)} paid</div>
                      </div>
                    </div>
                    <div className="text-sm flex justify-between items-center">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                        invoice.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                        invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                        invoice.status === 'voided' ? 'bg-gray-300 text-gray-700' :
                        invoice.status === 'payment_processing' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {invoice.status.replace(/_/g, ' ')}
                      </span>
                      <div className="text-xs text-gray-500">
                        Due: {formatDateDisplay(invoice.due_date)}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Enrolled By: {invoice.enrolled_by_parent_name || invoice.enrolled_by_parent_email || '—'}
                    </div>
                    <div className="mt-1">
                      <div className="text-sm text-gray-500">{formatDateInBusinessTZ(invoice.created_at)}</div>
                      <div className="text-xs text-gray-400">{formatTimeInBusinessTZ(invoice.created_at)}</div>
                    </div>
                    {selectedInvoiceId === invoice.id && (
                    <div className="mt-2 pt-2 border-t border-blue-100 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => copyInvoiceId(invoice.id)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                        title="Copy invoice ID to clipboard"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy ID
                      </button>
                      <button
                        onClick={() => handleOpenInvoiceDetailsModal(invoice)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Details
                      </button>
                      <button
                        onClick={() => handleOpenEditInvoiceModal(invoice)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </button>
                      {invoice.email_suppressed && invoice.status !== 'voided' && (
                        <button
                          onClick={() => handleSendInvoiceEmail(invoice)}
                          disabled={sendingInvoiceEmail === invoice.id}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-300 rounded-md hover:bg-amber-100 hover:text-amber-900 transition-colors disabled:opacity-50"
                          title="Email was held on create — send invoice notification now"
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          {sendingInvoiceEmail === invoice.id ? 'Sending…' : 'Send Email'}
                        </button>
                      )}
                      {['pending', 'partial', 'overdue'].includes(invoice.status) && (
                        <button
                          onClick={() => handleOpenManualPaymentModal(invoice)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                        >
                          <CreditCard className="h-3 w-3 mr-1" />
                          Mark Paid
                        </button>
                      )}
                      {['pending', 'partial', 'overdue'].includes(invoice.status) && (
                        <button
                          onClick={() => handleCompInvoice(invoice)}
                          disabled={compingInvoice === invoice.id}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 hover:text-purple-900 transition-colors disabled:opacity-50"
                          title="Comp to $0 — adds a no-charge discount and marks paid, keeping the original line items"
                        >
                          <Gift className="h-3 w-3 mr-1" />
                          {compingInvoice === invoice.id ? '…' : 'Comp'}
                        </button>
                      )}
                      {invoice.status === 'pending' && isInvoiceLate(invoice.due_date) && (
                        (() => {
                          const hasFirstLateFee = invoicesWithLateFees.has(invoice.id);
                          const hasSecondLateFee = invoicesWithSecondLateFees.has(invoice.id);
                          const isProcessing = processingLateFees.has(invoice.id);
                          const actionType = lateFeeActions.get(invoice.id);

                          if (hasSecondLateFee) {
                            // Green "Waive Late Fee" button for second late fee
                            return (
                              <button
                                onClick={() => handleRemoveLateFee(invoice)}
                                disabled={isProcessing}
                                className={`inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md transition-all duration-1000 ${
                                  isProcessing && actionType === 'waiving'
                                    ? 'cursor-not-allowed bg-gradient-to-r from-green-100 via-emerald-100 to-green-200 border-green-400 scale-140 shadow-2xl shadow-green-300/50 ring-6 ring-green-200/60' 
                                    : 'hover:bg-green-100 hover:text-green-900 hover:scale-110 hover:shadow-lg'
                                }`}
                                title="Waive late fee"
                                style={isProcessing && actionType === 'waiving' ? { 
                                  animation: 'pulse 2s infinite ease-in-out, ping 3s infinite ease-out, gradientShift 4s infinite ease-in-out',
                                  transform: 'scale(1.4)',
                                  background: 'linear-gradient(45deg, #dcfce7, #bbf7d0, #86efac, #bbf7d0, #dcfce7)',
                                  backgroundSize: '400% 400%'
                                } : {}}
                              >
                                <DollarSign className={`h-3 w-3 mr-1 ${isProcessing && actionType === 'waiving' ? 'animate-pulse' : ''}`} style={isProcessing && actionType === 'waiving' ? { animationDuration: '1.5s' } : {}} />
                                <span className={`${isProcessing && actionType === 'waiving' ? 'font-bold text-green-700' : ''}`} style={isProcessing && actionType === 'waiving' ? { animation: 'gentleFloat 2s infinite ease-in-out' } : {}}>
                                  {isProcessing && actionType === 'waiving' ? '🕊️ WAIVING... 🕊️' : 'Waive Late Fee'}
                                </span>
                                {isProcessing && actionType === 'waiving' && (
                                  <>
                                    <span className="ml-1 text-green-400" style={{ animation: 'gentleFloat 2.5s infinite ease-in-out' }}>🌸</span>
                                    <span className="ml-1 text-green-500" style={{ animation: 'gentleFloat 3s infinite ease-in-out' }}>✨</span>
                                    <span className="ml-1 text-green-600" style={{ animation: 'gentleFloat 1.8s infinite ease-in-out' }}>🕊️</span>
                                  </>
                                )}
                              </button>
                            );
                          } else if (hasFirstLateFee) {
                            // Red background button for first late fee (can escalate to second)
                            return (
                              <button
                                onClick={() => handleAddLateFee(invoice)}
                                disabled={isProcessing}
                                className={`inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-red-600 border border-red-600 rounded-md transition-all duration-500 ${
                                  isProcessing && actionType === 'escalating'
                                    ? 'cursor-not-allowed animate-pulse bg-gradient-to-r from-red-500 via-red-600 to-red-700 border-red-800 scale-150 shadow-2xl shadow-red-600/80 ring-8 ring-red-400/70 animate-ping transform rotate-3' 
                                    : 'hover:bg-red-700 hover:border-red-700 hover:scale-110 hover:shadow-xl'
                                }`}
                                title={`Escalate to ${formatLateFeeRate(LATE_FEE.secondRate)} late fee`}
                                style={isProcessing && actionType === 'escalating' ? { 
                                  animation: 'pulse 0.5s infinite, ping 1s infinite, bounce 0.6s infinite',
                                  transform: 'scale(1.5) rotate(3deg)'
                                } : {}}
                              >
                                <Clock className={`h-3 w-3 mr-1 ${isProcessing && actionType === 'escalating' ? 'animate-spin animate-bounce' : ''}`} style={isProcessing && actionType === 'escalating' ? { animationDuration: '0.3s' } : {}} />
                                <span className={`${isProcessing && actionType === 'escalating' ? 'animate-bounce font-bold text-red-100' : ''}`} style={isProcessing && actionType === 'escalating' ? { animationDuration: '0.4s' } : {}}>
                                  {isProcessing && actionType === 'escalating' ? '🚨 ESCALATING! 🚨' : 'More Late Fee'}
                                </span>
                                {isProcessing && actionType === 'escalating' && (
                                  <>
                                    <span className="ml-1 text-red-200 animate-ping">⚡</span>
                                    <span className="ml-1 text-red-300 animate-bounce">💸</span>
                                    <span className="ml-1 text-red-400 animate-pulse">🔥</span>
                                  </>
                                )}
                              </button>
                            );
                          } else {
                            // Light red button for no late fee (can add first)
                            return (
                              <button
                                onClick={() => handleAddLateFee(invoice)}
                                disabled={isProcessing}
                                className={`inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md transition-all duration-500 ${
                                  isProcessing && actionType === 'adding'
                                    ? 'cursor-not-allowed animate-pulse bg-gradient-to-r from-red-50 via-red-100 to-red-150 border-red-400 scale-130 shadow-xl shadow-red-300/70 ring-4 ring-red-300/60 animate-ping' 
                                    : 'hover:bg-red-100 hover:text-red-900 hover:scale-110 hover:shadow-lg'
                                }`}
                                title={`Add ${formatLateFeeRate(LATE_FEE.firstRate)} late fee`}
                                style={isProcessing && actionType === 'adding' ? { 
                                  animation: 'pulse 0.6s infinite, ping 1.2s infinite',
                                  transform: 'scale(1.3)'
                                } : {}}
                              >
                                <Clock className={`h-3 w-3 mr-1 ${isProcessing && actionType === 'adding' ? 'animate-spin animate-bounce' : ''}`} style={isProcessing && actionType === 'adding' ? { animationDuration: '0.7s' } : {}} />
                                <span className={`${isProcessing && actionType === 'adding' ? 'animate-bounce font-bold text-red-700' : ''}`} style={isProcessing && actionType === 'adding' ? { animationDuration: '0.9s' } : {}}>
                                  {isProcessing && actionType === 'adding' ? '⚡ CHARGING! ⚡' : 'Late Fee'}
                                </span>
                                {isProcessing && actionType === 'adding' && (
                                  <>
                                    <span className="ml-1 text-red-500 animate-ping">⏰</span>
                                    <span className="ml-1 text-red-600 animate-bounce">💳</span>
                                    <span className="ml-1 text-red-700 animate-pulse">💥</span>
                                  </>
                                )}
                              </button>
                            );
                          }
                        })()
                      )}
                      {(invoicesWithLateFees.has(invoice.id) || invoicesWithSecondLateFees.has(invoice.id)) && (
                        <button
                          onClick={() => handleSendLateFeeNotice(invoice)}
                          disabled={sendingLateFeeNotice === invoice.id || !invoice.enrolled_by_parent_email}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={invoice.enrolled_by_parent_email ? 'Email the late fee notice' : 'No parent email available'}
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          {sendingLateFeeNotice === invoice.id ? 'Sending...' : 'Late Fee Notice'}
                        </button>
                      )}
                    </div>
                    )}
                  </div>
                ))}
                {!invoicesLoading && invoicesData.length === 0 && (
                  <div className="text-center py-6 text-sm text-gray-500">
                    No invoices found.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {activeTab === 'carts' && <CartsPanel onOpenCart={setOpenCartId} />}

        {activeTab === 'refunds' && (
          <div className="lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
            {/* Filters */}
            <div className="px-4 py-2 border-b border-gray-200 flex-shrink-0 space-y-2">
              <DateRangeFilter
                idPrefix="refund"
                value={refundFilters}
                onChange={(range) => {
                  setRefundFilters(prev => ({ ...prev, ...range }));
                }}
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div>
                  <label htmlFor="refund-search" className="block text-xs font-medium text-gray-600">Search</label>
                  <input type="text" id="refund-search" className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={refundFilters.search} onChange={(e) => setRefundFilters({ ...refundFilters, search: e.target.value })} placeholder="Student or program" />
                </div>
                <div>
                  <label htmlFor="refund-status" className="block text-xs font-medium text-gray-600">Status</label>
                  <select id="refund-status" className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={refundFilters.status} onChange={(e) => setRefundFilters({ ...refundFilters, status: e.target.value })}>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label htmlFor="refund-brand" className="block text-xs font-medium text-gray-600">Brand</label>
                  <BrandFilterSelect id="refund-brand" value={brandFilter} onChange={setBrandFilter} className="mt-0.5 block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" />
                </div>
              </div>
            </div>

            {/* Refunds Info Bar */}
            <div className="px-4 py-2 flex items-center text-sm text-gray-600 flex-shrink-0 border-b border-gray-100">
              {refundsLoading ? (
                <span className="flex items-center gap-1.5">
                  <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                  Loading refunds...
                </span>
              ) : (
                `${filteredRefunds.length} refund${filteredRefunds.length !== 1 ? 's' : ''}`
              )}
            </div>

            {/* Refunds Table */}
            <div className={`overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0 transition-opacity${refundsLoading ? ' opacity-60' : ''}`}>
              <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => setRefundSort({
                        column: 'student_name',
                        direction: refundSort.column === 'student_name' && refundSort.direction === 'asc' ? 'desc' : 'asc'
                      })}
                    >
                      Student
                      {refundSort.column === 'student_name' && (
                        <span className="ml-1">{refundSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => setRefundSort({
                        column: 'program_name',
                        direction: refundSort.column === 'program_name' && refundSort.direction === 'asc' ? 'desc' : 'asc'
                      })}
                    >
                      Program
                      {refundSort.column === 'program_name' && (
                        <span className="ml-1">{refundSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Period
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Paid By
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Method
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => setRefundSort({
                        column: 'amount',
                        direction: refundSort.column === 'amount' && refundSort.direction === 'asc' ? 'desc' : 'asc'
                      })}
                    >
                      Amount
                      {refundSort.column === 'amount' && (
                        <span className="ml-1">{refundSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => setRefundSort({
                        column: 'refund_date',
                        direction: refundSort.column === 'refund_date' && refundSort.direction === 'asc' ? 'desc' : 'asc'
                      })}
                    >
                      Refund Date
                      {refundSort.column === 'refund_date' && (
                        <span className="ml-1">{refundSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => setRefundSort({
                        column: 'created_at',
                        direction: refundSort.column === 'created_at' && refundSort.direction === 'asc' ? 'desc' : 'asc'
                      })}
                    >
                      Created
                      {refundSort.column === 'created_at' && (
                        <span className="ml-1">{refundSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRefunds.map((refund) => (
                    <React.Fragment key={refund.id}>
                      <tr
                        className={selectedRefundId === refund.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'}
                        onClick={() => setSelectedRefundId(selectedRefundId === refund.id ? null : refund.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {refund.student_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {refund.program_name} <AdminBrandTag brand={refund.brand_key} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatPeriodRange(refund.period_start, refund.period_end)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {refund.paid_by_parent_name || refund.paid_by_parent_email || (refund.paid_by_parent_id ? `Parent ${refund.paid_by_parent_id.slice(0, 8)}…` : '—')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {refund.refund_method.charAt(0).toUpperCase() + refund.refund_method.slice(1)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${refund.amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              refund.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : refund.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : refund.status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {refund.status.charAt(0).toUpperCase() + refund.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateDisplay(refund.refund_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDateInBusinessTZ(refund.created_at)}</div>
                          <div className="text-xs text-gray-400">{formatTimeInBusinessTZ(refund.created_at)}</div>
                        </td>
                      </tr>
                      {selectedRefundId === refund.id && (
                        <tr className="bg-blue-50">
                          <td colSpan={9} className="px-6 py-2 border-b border-blue-100">
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); copyPaymentId(refund.payment_id); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                title="Copy payment ID to clipboard"
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Copy ID
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {!refundsLoading && filteredRefunds.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-6 py-4 text-center text-sm text-gray-500">
                        No refunds found matching the filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Mobile Card View for Refunds */}
              <div className="lg:hidden">
                {filteredRefunds.map((refund) => (
                  <div
                    key={refund.id}
                    className={`border-b border-gray-200 p-3 cursor-pointer ${selectedRefundId === refund.id ? 'bg-blue-50' : ''}`}
                    onClick={() => setSelectedRefundId(selectedRefundId === refund.id ? null : refund.id)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="text-base font-bold text-gray-900">{refund.student_name}</div>
                        <div className="text-sm text-gray-600">{refund.program_name} <AdminBrandTag brand={refund.brand_key} /></div>
                        <div className="text-xs text-gray-500">{formatPeriodRange(refund.period_start, refund.period_end)}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Paid By: {refund.paid_by_parent_name || refund.paid_by_parent_email || (refund.paid_by_parent_id ? `Parent ${refund.paid_by_parent_id.slice(0, 8)}…` : '—')}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">${refund.amount.toFixed(2)}</div>
                      </div>
                    </div>
                    <div className="text-sm flex justify-between items-center">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        refund.status === 'completed' ? 'bg-green-100 text-green-800' :
                        refund.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        refund.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {refund.status}
                      </span>
                      <div className="text-xs text-gray-500">
                        {refund.refund_method}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Refund Date: {formatDateDisplay(refund.refund_date)}
                    </div>
                    <div className="mt-1">
                      <div className="text-sm text-gray-500">{formatDateInBusinessTZ(refund.created_at)}</div>
                      <div className="text-xs text-gray-400">{formatTimeInBusinessTZ(refund.created_at)}</div>
                    </div>
                    {selectedRefundId === refund.id && (
                      <div className="mt-2 pt-2 border-t border-blue-100 flex flex-wrap gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); copyPaymentId(refund.payment_id); }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                          title="Copy payment ID to clipboard"
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copy ID
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {!refundsLoading && filteredRefunds.length === 0 && (
                  <div className="text-center py-6 text-sm text-gray-500">
                    No refunds found.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Debt Tab - Invoices N+ days overdue */}
        {activeTab === 'debt' && (
          <div className="lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
            {/* Header info */}
            <div className="p-4 border-b border-gray-200 bg-red-50 flex-shrink-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Overdue Invoices ({debtDaysDisplay}+ days past due)</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex flex-col gap-1">
                    <label htmlFor="debt-brand" className="text-sm font-medium text-red-800">Brand</label>
                    <BrandFilterSelect id="debt-brand" value={brandFilter} onChange={setBrandFilter} />
                  </div>
                  {/* Send All Reminders Button */}
                  <button
                    onClick={handleSendBulkPaymentReminders}
                    disabled={sendingBulkReminders || debtData.length === 0}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    title="Send payment reminder emails to all parents with overdue invoices"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {sendingBulkReminders ? 'Sending...' : `Send All (${debtData.filter(inv => inv.enrolled_by_parent_email).length})`}
                  </button>
                  <div className="flex flex-col gap-1 min-w-[200px]">
                    <label htmlFor="debt-days-slider" className="text-sm font-medium text-red-800">
                      Days overdue: {debtDaysDisplay}
                    </label>
                    <input
                      id="debt-days-slider"
                      type="range"
                      min="1"
                      max="90"
                      value={debtDaysDisplay}
                      onChange={(e) => {
                        // Update display immediately for visual feedback while dragging
                        setDebtDaysDisplay(Number(e.target.value));
                      }}
                      onMouseUp={(e) => {
                        // Only trigger query when user releases the slider
                        const value = Number((e.target as HTMLInputElement).value);
                        if (value !== debtDaysThreshold) {
                          setDebtDaysThreshold(value);
                          setDebtPage(1);
                        }
                      }}
                      onTouchEnd={(e) => {
                        // Handle touch devices
                        const value = Number((e.target as HTMLInputElement).value);
                        if (value !== debtDaysThreshold) {
                          setDebtDaysThreshold(value);
                          setDebtPage(1);
                        }
                      }}
                      className="w-full cursor-pointer"
                      style={{ accentColor: '#dc2626' }}
                    />
                    <div className="flex justify-between text-xs text-red-600">
                      <span>1 day</span>
                      <span>90 days</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Reminder Toast Notification */}
            {reminderToast && (
              <div className={`p-3 flex-shrink-0 ${reminderToast.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border-b`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{reminderToast.type === 'success' ? '✅' : '❌'}</span>
                    <span className={`text-sm ${reminderToast.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                      {reminderToast.message}
                    </span>
                  </div>
                  <button
                    onClick={() => setReminderToast(null)}
                    className={`text-sm ${reminderToast.type === 'success' ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'}`}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Debt Top Pagination */}
            <div className="p-4 pb-2 flex flex-col sm:flex-row items-center justify-between gap-3 flex-shrink-0">
              {(() => {
                const totalPages = Math.max(1, Math.ceil(debtTotal / PAGE_SIZE));
                const start = (debtPage - 1) * PAGE_SIZE + 1;
                const end = Math.min(debtPage * PAGE_SIZE, debtTotal);
                return (
                  <>
                    <div className="text-sm text-gray-600 flex items-center gap-1.5">
                      {debtLoading ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                          Loading...
                        </>
                      ) : (
                        `Showing ${debtTotal === 0 ? 0 : start} to ${end} of ${debtTotal} overdue invoices`
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setDebtPage(1)}
                        disabled={debtPage === 1}
                      >
                        First
                      </button>
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setDebtPage(Math.max(1, debtPage - 1))}
                        disabled={debtPage === 1}
                      >
                        Prev
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {debtPage} of {totalPages}
                      </span>
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setDebtPage(Math.min(totalPages, debtPage + 1))}
                        disabled={debtPage === totalPages}
                      >
                        Next
                      </button>
                      <button
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                        onClick={() => setDebtPage(totalPages)}
                        disabled={debtPage === totalPages}
                      >
                        Last
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Debt Table */}
            <div className="overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0">
              <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => {
                        setDebtPage(1);
                        setDebtSort({
                          column: 'student_name',
                          direction: debtSort.column === 'student_name' && debtSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Student
                      {debtSort.column === 'student_name' && (
                        <span className="ml-1">{debtSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => {
                        setDebtPage(1);
                        setDebtSort({
                          column: 'program_name',
                          direction: debtSort.column === 'program_name' && debtSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Program
                      {debtSort.column === 'program_name' && (
                        <span className="ml-1">{debtSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Contact
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => {
                        setDebtPage(1);
                        setDebtSort({
                          column: 'amount',
                          direction: debtSort.column === 'amount' && debtSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Amount Due
                      {debtSort.column === 'amount' && (
                        <span className="ml-1">{debtSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => {
                        setDebtPage(1);
                        setDebtSort({
                          column: 'due_date',
                          direction: debtSort.column === 'due_date' && debtSort.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                    >
                      Due Date
                      {debtSort.column === 'due_date' && (
                        <span className="ml-1">{debtSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Days Overdue
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {debtData.map((invoice) => {
                    const daysOverdue = Math.floor((new Date().getTime() - new Date(invoice.due_date).getTime()) / (1000 * 60 * 60 * 24));
                    const remainingAmount = invoice.total_amount - invoice.amount_paid;
                    return (
                      <React.Fragment key={invoice.id}>
                        <tr className={selectedDebtId === invoice.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-red-50 cursor-pointer'} onClick={() => setSelectedDebtId(selectedDebtId === invoice.id ? null : invoice.id)}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {invoice.student_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invoice.program_name} <AdminBrandTag brand={invoice.brand_key} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invoice.enrolled_by_parent_name || invoice.enrolled_by_parent_email || '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="font-semibold text-red-600">${remainingAmount.toFixed(2)}</div>
                            <div className="text-xs text-gray-500">
                              (${invoice.amount_paid.toFixed(2)} of ${invoice.total_amount.toFixed(2)} paid)
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                invoice.status === 'overdue'
                                  ? 'bg-red-100 text-red-800'
                                  : invoice.status === 'partial'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDateDisplay(invoice.due_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`text-sm font-medium ${daysOverdue > 60 ? 'text-red-700' : daysOverdue > 45 ? 'text-red-600' : 'text-red-500'}`}>
                              {daysOverdue} days
                            </span>
                          </td>
                        </tr>
                        {selectedDebtId === invoice.id && (
                          <tr>
                            <td colSpan={7} className="px-6 py-2 bg-blue-50 border-t border-blue-100">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); copyInvoiceId(invoice.id); }}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                                  title="Copy invoice ID to clipboard"
                                >
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy ID
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleOpenInvoiceDetailsModal(invoice); }}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  View
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSendPaymentReminder(invoice); }}
                                  disabled={sendingReminder === invoice.id || !invoice.enrolled_by_parent_email}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={invoice.enrolled_by_parent_email ? `Send payment reminder to ${invoice.enrolled_by_parent_email}` : 'No parent email available'}
                                >
                                  <Mail className="h-3 w-3 mr-1" />
                                  {sendingReminder === invoice.id ? 'Sending...' : 'Remind'}
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleOpenManualPaymentModal(invoice); }}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                                >
                                  <CreditCard className="h-3 w-3 mr-1" />
                                  Mark Paid
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleVoidInvoice(invoice); }}
                                  disabled={voidingInvoice === invoice.id}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors disabled:opacity-50"
                                  title="Void this invoice (mark as uncollectible)"
                                >
                                  <Ban className="h-3 w-3 mr-1" />
                                  {voidingInvoice === invoice.id ? 'Voiding...' : 'Void'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!debtLoading && debtData.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                        No overdue invoices found ({debtDaysDisplay}+ days past due).
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Mobile Card View for Debt */}
              <div className="lg:hidden">
                {debtData.map((invoice) => {
                  const daysOverdue = Math.floor((new Date().getTime() - new Date(invoice.due_date).getTime()) / (1000 * 60 * 60 * 24));
                  const remainingAmount = invoice.total_amount - invoice.amount_paid;
                  return (
                    <div key={invoice.id} className={`border-b border-gray-200 p-3 cursor-pointer ${selectedDebtId === invoice.id ? 'bg-blue-50' : 'bg-red-50'}`} onClick={() => setSelectedDebtId(selectedDebtId === invoice.id ? null : invoice.id)}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="text-base font-bold text-gray-900">{invoice.student_name}</div>
                          <div className="text-sm text-gray-600">{invoice.program_name} <AdminBrandTag brand={invoice.brand_key} /></div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-red-600">${remainingAmount.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">${invoice.amount_paid.toFixed(2)} paid</div>
                        </div>
                      </div>
                      <div className="text-sm flex justify-between items-center">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                          invoice.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.status}
                        </span>
                        <div className="text-xs font-medium text-red-600">
                          {daysOverdue} days overdue
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Due: {formatDateDisplay(invoice.due_date)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Contact: {invoice.enrolled_by_parent_name || invoice.enrolled_by_parent_email || '—'}
                      </div>
                      {selectedDebtId === invoice.id && (
                        <div className="mt-2 pt-2 border-t border-blue-100 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => copyInvoiceId(invoice.id)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                            title="Copy invoice ID to clipboard"
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Copy ID
                          </button>
                          <button
                            onClick={() => handleOpenInvoiceDetailsModal(invoice)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </button>
                          <button
                            onClick={() => handleSendPaymentReminder(invoice)}
                            disabled={sendingReminder === invoice.id || !invoice.enrolled_by_parent_email}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={invoice.enrolled_by_parent_email ? 'Send payment reminder' : 'No email'}
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            {sendingReminder === invoice.id ? '...' : 'Remind'}
                          </button>
                          <button
                            onClick={() => handleOpenManualPaymentModal(invoice)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                          >
                            <CreditCard className="h-3 w-3 mr-1" />
                            Mark Paid
                          </button>
                          <button
                            onClick={() => handleVoidInvoice(invoice)}
                            disabled={voidingInvoice === invoice.id}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors disabled:opacity-50"
                            title="Void this invoice"
                          >
                            <Ban className="h-3 w-3 mr-1" />
                            {voidingInvoice === invoice.id ? '...' : 'Void'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!debtLoading && debtData.length === 0 && (
                  <div className="text-center py-6 text-sm text-gray-500">
                    No overdue invoices found ({debtDaysDisplay}+ days past due).
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        <CartDetailModal
          cartId={openCartId}
          onClose={() => setOpenCartId(null)}
          onRefundPayment={handleRefundCartPayment}
          onChanged={() => { fetchPayments(); fetchInvoices(); }}
        />

        {/* Refund Modal */}
        <Modal
          isOpen={refundModal.isOpen}
          onClose={handleCloseRefundModal}
          title="Process Refund"
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">
                Student: {refundModal.studentName}
              </p>
              <p className="text-sm text-gray-500">
                Program: {refundModal.programName}
              </p>
              <p className="text-sm text-gray-500">
                Original Payment: ${refundModal.paymentAmount.toFixed(2)}
              </p>
              {refundModal.cartId && (
                <p className="mt-2 text-xs text-indigo-800 bg-indigo-50 border border-indigo-100 rounded p-2" data-testid="refund-cart-note">
                  This is one invoice&apos;s share of a bundle payment. Refunding here refunds only this invoice&apos;s
                  share; to refund the whole payment, open it from the Bundle chip.
                </p>
              )}
            </div>
            <div>
              <label htmlFor="refund-amount" className="block text-sm font-medium text-gray-700">
                Refund Amount
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={refundModal.paymentAmount}
                  id="refund-amount"
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                  value={refundModal.amount}
                  onChange={(e) => setRefundModal({ ...refundModal, amount: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div>
              <label htmlFor="refund-method" className="block text-sm font-medium text-gray-700">
                Refund Method
              </label>
              <select
                id="refund-method"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={refundModal.refundMethod}
                onChange={(e) => setRefundModal({ ...refundModal, refundMethod: e.target.value })}
              >
                {refundModal.paymentMethod === 'card' && (
                  <option value="card">Credit Card (Stripe)</option>
                )}
                <option value="manual">Manual Refund</option>
                {refundModal.parentId && (
                  <option value="wallet">BHCS CASH (Wallet)</option>
                )}
              </select>
            </div>
            {refundModal.refundMethod === 'wallet' && (
              <div>
                <label htmlFor="refund-bucket" className="block text-sm font-medium text-gray-700">
                  Wallet Bucket
                </label>
                <select
                  id="refund-bucket"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  value={refundModal.bucket}
                  onChange={(e) => setRefundModal({ ...refundModal, bucket: e.target.value as 'withdrawable' | 'non_withdrawable' })}
                >
                  <option value="non_withdrawable">Non-withdrawable (bonus credit, invoice-only)</option>
                  <option value="withdrawable">Withdrawable (refundable as cash)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Use withdrawable for original cash/card/Zelle payments. Use non-withdrawable for promotional credits.
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  The refund is also recorded against this payment: the invoice gets a matching negative
                  line item and its total and amount paid are adjusted automatically.
                </p>
              </div>
            )}
            <div>
              <label htmlFor="refund-notes" className="block text-sm font-medium text-gray-700">
                Refund Reason
              </label>
              <textarea
                id="refund-notes"
                rows={3}
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={refundModal.refundNotes}
                onChange={(e) => setRefundModal({ ...refundModal, refundNotes: e.target.value })}
                placeholder={refundModal.refundMethod === 'wallet'
                  ? "Required: enter reason for crediting this amount to BHCS CASH (e.g., cancellation refund — Spring 2026)"
                  : refundModal.refundMethod === 'manual'
                    ? "Enter details about how this refund was processed manually"
                    : "Enter reason for the refund (e.g., customer request, course cancellation)"}
              />
            </div>
            {refundModal.error && (
              <div className="text-red-600 text-sm">{refundModal.error}</div>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCloseRefundModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                disabled={refundModal.isProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProcessRefund}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={refundModal.isProcessing}
              >
                {refundModal.isProcessing ? 'Processing...' : 'Process Refund'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Recurring Monthly Invoice Batch Modal */}
        <Modal
          isOpen={showBatchModal}
          onClose={() => setShowBatchModal(false)}
          title="Run Recurring Monthly Invoices"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="batch-month" className="block text-sm font-medium text-gray-700">Month</label>
                <input
                  id="batch-month"
                  type="month"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  value={batchMonth}
                  onChange={(e) => setBatchMonth(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="batch-limit" className="block text-sm font-medium text-gray-700">Limit (optional)</label>
                <input
                  id="batch-limit"
                  type="number"
                  min={1}
                  placeholder={batchStudentId ? 'Not used' : 'e.g. 50'}
                  disabled={Boolean(batchStudentId)}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-400"
                  value={batchStudentId ? '' : batchLimit}
                  onChange={(e) => setBatchLimit(e.target.value.replace(/[^0-9]/g, ''))}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {batchStudentId
                    ? 'Ignored while a single student is selected.'
                    : 'Counts invoices actually created. Preview loads automatically once set.'}
                </p>
              </div>
              <BatchStudentPicker
                inputId="batch-student"
                enabled={showBatchModal}
                value={batchStudentId}
                onChange={(id) => setBatchStudentId(id)}
                helpText="Scopes the run to one student and ignores the limit."
              />
              <div>
                <label htmlFor="batch-proration-override" className="block text-sm font-medium text-gray-700">Proration override (0–1, optional)</label>
                <input
                  id="batch-proration-override"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={0}
                  max={1}
                  placeholder="e.g. 0.75"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  value={batchProrationOverride}
                  onChange={(e) => setBatchProrationOverride(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  If provided, this fraction overrides computed scheduled-days proration for this run. Materials are not prorated.
                </p>
              </div>
              <div className="flex items-end col-span-1 sm:col-span-3">
                <div className="flex flex-col sm:flex-row gap-3 w-full items-start sm:items-center">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      checked={batchSuppressEmail}
                      onChange={(e) => setBatchSuppressEmail(e.target.checked)}
                    />
                    Hold emails
                  </label>
                  {batchSuppressEmail && (
                    <p className="text-xs text-amber-700">Invoices will be created but parents won't be notified. Use "Send Email" on each invoice when ready.</p>
                  )}
                </div>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  onClick={() => runMonthlyInvoices()}
                  disabled={runMonthlyInvoicesMutation.isPending || !batchMonth}
                >
                  {runMonthlyInvoicesMutation.isPending ? 'Running…' : 'Run'}
                </button>
              </div>
            </div>

    {/* Preview */}
    {batchLimit || batchStudentId ? (
      <div className="pt-2 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Preview{previewTotalCandidates != null ? ` (${previewItems.length} of ${previewTotalCandidates})` : ''}
          {previewItems.some((p) => p.would_create === false) ? (
            <span className="ml-2 font-normal text-gray-500">
              — {previewItems.filter((p) => p.would_create !== false).length} would create an invoice
            </span>
          ) : null}
        </h4>
        {isLoadingPreview ? (
          <p className="text-sm text-gray-500">Loading preview…</p>
        ) : previewError ? (
          <p className="text-sm text-red-600">{previewError}</p>
        ) : previewItems.length === 0 ? (
          <p className="text-sm text-gray-500">No eligible enrollments for the selected month.</p>
        ) : (
          <div className="max-h-64 overflow-auto rounded border border-gray-200 bg-gray-50">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500">
                  <th className="text-left py-1 px-2">Enrollment</th>
                  <th className="text-left py-1 px-2">Email to</th>
                  <th className="text-left py-1 px-2">Line items</th>
                  <th className="text-right py-1 px-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {previewItems.map((p) => {
                  const info = enrollmentInfoCache.get(p.enrollment_id);
                  const student = info ? info.studentName : '—';
                  const program = info ? info.programName : '—';
                  return (
                    <tr key={p.enrollment_id} className="border-t border-gray-200">
                      <td className="py-1 px-2 text-gray-800">
                        <div className="font-medium">{student}</div>
                        <div className="text-gray-600">{program}</div>
                        {p.would_create === false ? (
                          <span className="mt-0.5 inline-block rounded bg-gray-200 text-gray-700 px-1 py-0.5 text-[10px]">
                            no invoice
                          </span>
                        ) : null}
                      </td>
                      <td className="py-1 px-2 text-gray-800">
                        {p.email_to || '—'}
                      </td>
                      <td className="py-1 px-2 text-gray-800">
                        {Array.isArray(p.line_items) && p.line_items.length > 0 ? (
                          <ul className="list-disc pl-4 space-y-0.5">
                            {p.line_items.map((li, idx) => (
                              <li key={idx}>
                                <span className="text-gray-700">{li.name}</span>{' '}
                                {typeof li.proration_factor === 'number' && li.proration_factor > 0 && li.proration_factor < 1 ? (
                                  <span className="ml-1 inline-block rounded bg-yellow-100 text-yellow-800 px-1 py-0.5 text-[10px] align-middle">
                                    prorated {(li.proration_factor * 100).toFixed(0)}%
                                  </span>
                                ) : null}{' '}
                                <span className="text-gray-600">(${Number.isFinite(li.amount) ? li.amount.toFixed(2) : '0.00'})</span>
                              </li>
                            ))}
                          </ul>
                        ) : '—'}
                      </td>
                      <td className="py-1 px-2 text-gray-800 text-right">
                        ${Number.isFinite(p.amount) ? p.amount.toFixed(2) : '0.00'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    ) : null}

            <div className="pt-2 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Results</h4>
              {batchRunResults.length === 0 ? (
                <p className="text-sm text-gray-500">No runs yet.</p>
              ) : (
                <ul className="space-y-2 max-h-96 overflow-auto">
                  {batchRunResults.map((r, idx) => {
                    const expanded = expandedRuns.find(er => er.index === idx)?.open ?? false;
                    const details = batchRunDetails[idx] || [];
                    return (
                      <li key={idx} className="text-sm bg-gray-50 rounded">
                        <button
                          type="button"
                          className="w-full text-left p-2 flex items-center justify-between hover:bg-gray-100"
                          onClick={() => {
                            setExpandedRuns(prev => {
                              const existing = prev.find(er => er.index === idx);
                              if (existing) {
                                return prev.map(er => er.index === idx ? { ...er, open: !er.open } : er);
                              }
                              return [{ index: idx, open: true, details }, ...prev];
                            })
                          }}
                        >
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-800">{formatDateTimeInBusinessTZ(r.timestamp)}</span>
                              <span className="text-gray-600">created: {r.created} · skipped: {r.skipped}</span>
                            </div>
                            {r.message ? (
                              <div className="text-gray-600">{r.message}</div>
                            ) : null}
                          </div>
                          <span className="ml-3 text-gray-500">{expanded ? '▾' : '▸'}</span>
                        </button>
                        {expanded && details.length > 0 && (
                          <div className="px-3 pb-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left py-1 pr-2">Enrollment</th>
                                  <th className="text-left py-1 pr-2">Processed</th>
                                  <th className="text-left py-1 pr-2">Invoice</th>
                                  <th className="text-left py-1 pr-2">Message/Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {details.map((d, i) => {
                                  const info = d.enrollment_id ? enrollmentInfoCache.get(d.enrollment_id) : undefined;
                                  const student = info ? info.studentName : '—';
                                  const program = info ? info.programName : '—';
                                  const status = d.success ? (d.skipped ? 'skipped' : 'created') : 'error';
                                  const amount = d.invoice_id ? (invoiceAmountCache.get(d.invoice_id) ?? null) : null;
                                  const invoiceHref = d.invoice_id ? `/invoices/${d.invoice_id}` : null;
                                  return (
                                    <tr key={i} className="border-t border-gray-200">
                                      <td className="py-1 pr-2 text-gray-800">
                                        <div className="font-medium">{student}</div>
                                        <div className="text-gray-600">{program}</div>
                                      </td>
                                      <td className="py-1 pr-2">{status}</td>
                                      <td className="py-1 pr-2 text-gray-700">
                                        {invoiceHref ? (
                                          <a href={invoiceHref} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                            {amount !== null ? `$${amount.toFixed(2)}` : 'View'}
                                          </a>
                                        ) : '—'}
                                      </td>
                                      <td className="py-1 pr-2 text-gray-700">{d.message || d.error || '—'}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </Modal>

        {/* Autopay Batch Modal */}
        <Modal
          isOpen={showAutopayModal}
          onClose={() => setShowAutopayModal(false)}
          title="Run Autopay"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="autopay-month" className="block text-sm font-medium text-gray-700">Month (optional)</label>
                <select
                  id="autopay-month"
                  className="mt-1 focus:ring-emerald-500 focus:border-emerald-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  value={autopayMonth}
                  onChange={(e) => setAutopayMonth(e.target.value)}
                >
                  <option value="">All outstanding months</option>
                  {autopayMonthOptions.map((m) => (
                    <option key={m} value={m}>{formatMonthLabel(m)}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {autopayMonth
                    ? 'Only invoices billed for this month are collected.'
                    : autopayMonthsLoaded && autopayMonthOptions.length === 0
                      ? 'No monthly-billed invoices are outstanding — autopay has nothing to collect.'
                      : 'Every outstanding month is collected.'}
                </p>
              </div>
              <div>
                <label htmlFor="autopay-limit" className="block text-sm font-medium text-gray-700">Limit (optional)</label>
                <input
                  id="autopay-limit"
                  type="number"
                  min={1}
                  placeholder={autopayStudentId ? 'Not used' : 'e.g. 100'}
                  disabled={Boolean(autopayStudentId)}
                  className="mt-1 focus:ring-emerald-500 focus:border-emerald-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-400"
                  value={autopayStudentId ? '' : autopayLimit}
                  onChange={(e) => setAutopayLimit(e.target.value.replace(/[^0-9]/g, ''))}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {autopayStudentId
                    ? 'Ignored while a single student is selected.'
                    : 'Counts invoices actually charged — the rows the preview lists as "would charge".'}
                </p>
              </div>
              <BatchStudentPicker
                inputId="autopay-student"
                enabled={showAutopayModal}
                value={autopayStudentId}
                onChange={(id) => setAutopayStudentId(id)}
                helpText="Collects only this student's invoices and ignores the limit."
              />
              <div className="sm:col-span-3 flex items-end justify-end">
                <button
                  type="button"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                  onClick={() => runAutopay()}
                  disabled={runAutopayMutation.isPending}
                >
                  {runAutopayMutation.isPending ? 'Running…' : 'Run autopay'}
                </button>
              </div>
            </div>

            {/* Preview */}
            {autopayLimit || autopayStudentId || autopayMonth ? (
              <div className="pt-2 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Preview ({autopayPreviewItems.length})
                  <span className="ml-2 font-normal text-gray-500">
                    — {autopayPreviewItems.filter((p) => p.would_charge).length} would be charged
                  </span>
                </h4>
                {isLoadingAutopayPreview ? (
                  <p className="text-sm text-gray-500">Loading preview…</p>
                ) : autopayPreviewError ? (
                  <p className="text-sm text-red-600">{autopayPreviewError}</p>
                ) : autopayPreviewItems.length === 0 ? (
                  <div className="text-sm text-gray-500">
                    <p>
                      Nothing for autopay to charge{autopayMonth ? ` in ${formatMonthLabel(autopayMonth)}` : ''}
                      {autopayStudentId ? ' for the selected student' : ''}.
                    </p>
                    <p className="mt-1 text-xs">
                      Autopay only covers monthly-billed programs where a parent has saved a card or
                      bank account. Fixed-term, per-day and flex invoices are never charged automatically.
                    </p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-auto rounded border border-gray-200 bg-gray-50">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left py-1 px-2">Enrollment</th>
                          <th className="text-left py-1 px-2">Parent</th>
                          <th className="text-left py-1 px-2">Method</th>
                          <th className="text-left py-1 px-2">Due</th>
                          <th className="text-right py-1 px-2">Due amt</th>
                          <th className="text-right py-1 px-2">BHCS Cash</th>
                          <th className="text-right py-1 px-2">Fee</th>
                          <th className="text-right py-1 px-2">Charge</th>
                          <th className="text-left py-1 px-2">Outcome</th>
                        </tr>
                      </thead>
                      <tbody>
                        {autopayPreviewItems.map((p) => {
                          const info = enrollmentInfoCache.get(p.enrollment_id);
                          const student = info ? info.studentName : '—';
                          const program = info ? info.programName : '—';
                          const outcome = p.would_charge ? 'would charge' : (p.reason || 'skip');
                          return (
                            <tr key={p.invoice_id} className="border-t border-gray-200">
                              <td className="py-1 px-2 text-gray-800">
                                <div className="font-medium">{student}</div>
                                <div className="text-gray-600">{program}</div>
                              </td>
                              <td className="py-1 px-2 text-gray-800">
                                <div>{p.parent_email || '—'}</div>
                                {p.stripe_customer_action === 'would_create' ? (
                                  <span className="mt-1 inline-block rounded bg-yellow-100 text-yellow-800 px-1 py-0.5 text-[10px]">
                                    would create Stripe customer
                                  </span>
                                ) : null}
                              </td>
                              <td className="py-1 px-2 text-gray-800">{p.payment_method || '—'}</td>
                              <td className="py-1 px-2 text-gray-800">{p.due_date ? formatDateInBusinessTZ(p.due_date) : '—'}</td>
                              <td className="py-1 px-2 text-gray-800 text-right">${Number.isFinite(p.amount_due) ? p.amount_due.toFixed(2) : '0.00'}</td>
                              <td className="py-1 px-2 text-gray-800 text-right">${typeof p.bhcs_cash_applied === 'number' && Number.isFinite(p.bhcs_cash_applied) ? p.bhcs_cash_applied.toFixed(2) : '0.00'}</td>
                              <td className="py-1 px-2 text-gray-800 text-right">${Number.isFinite(p.card_fee) ? p.card_fee.toFixed(2) : '0.00'}</td>
                              <td className="py-1 px-2 text-gray-800 text-right">${Number.isFinite(p.amount_to_charge) ? p.amount_to_charge.toFixed(2) : '0.00'}</td>
                              <td className="py-1 px-2 text-gray-800">
                                {outcome}
                                {p.existing_payment ? <span className="ml-2 text-gray-500">(existing payment)</span> : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}

            {/* Results */}
            <div className="pt-2 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Runs</h4>
              {autopayRunResults.length === 0 ? (
                <p className="text-sm text-gray-500">No runs yet.</p>
              ) : (
                <ul className="space-y-2 max-h-96 overflow-auto">
                  {autopayRunResults.map((r, idx) => {
                    const expanded = expandedAutopayRuns.find(er => er.index === idx)?.open ?? false;
                    const details = autopayRunDetails[idx] || [];
                    return (
                      <li key={idx} className="text-sm bg-gray-50 rounded">
                        <button
                          type="button"
                          className="w-full text-left p-2 flex items-center justify-between hover:bg-gray-100"
                          onClick={() => {
                            setExpandedAutopayRuns(prev => {
                              const existing = prev.find(er => er.index === idx);
                              if (existing) {
                                return prev.map(er => er.index === idx ? { ...er, open: !er.open } : er);
                              }
                              return [{ index: idx, open: true, details }, ...prev];
                            })
                          }}
                        >
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-800">{formatDateTimeInBusinessTZ(r.timestamp)}</span>
                              <span className="text-gray-600">charged: {r.charged} · skipped: {r.skipped} · failures: {r.failures}</span>
                            </div>
                            {r.message ? (
                              <div className="text-gray-600">{r.message}</div>
                            ) : null}
                          </div>
                          <span className="ml-3 text-gray-500">{expanded ? '▾' : '▸'}</span>
                        </button>
                        {expanded && details.length > 0 && (
                          <div className="px-3 pb-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left py-1 pr-2">Enrollment</th>
                                  <th className="text-left py-1 pr-2">Outcome</th>
                                  <th className="text-left py-1 pr-2">Invoice</th>
                                  <th className="text-left py-1 pr-2">Message/Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {details.map((d, i) => {
                                  const info = d.enrollment_id ? enrollmentInfoCache.get(d.enrollment_id) : undefined;
                                  const student = info ? info.studentName : '—';
                                  const program = info ? info.programName : '—';
                                  const status = d.success ? 'charged' : (d.skipped ? 'skipped' : 'error');
                                  const invoiceHref = d.invoice_id ? `/invoices/${d.invoice_id}` : null;
                                  const linkText = typeof d.amount_to_charge === 'number'
                                    ? `$${d.amount_to_charge.toFixed(2)}`
                                    : 'View';
                                  return (
                                    <tr key={i} className="border-t border-gray-200">
                                      <td className="py-1 pr-2 text-gray-800">
                                        <div className="font-medium">{student}</div>
                                        <div className="text-gray-600">{program}</div>
                                      </td>
                                      <td className="py-1 pr-2">{status}</td>
                                      <td className="py-1 pr-2 text-gray-700">
                                        {invoiceHref ? (
                                          <a href={invoiceHref} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                            {linkText}
                                          </a>
                                        ) : '—'}
                                      </td>
                                      <td className="py-1 pr-2 text-gray-700">{d.reason || d.error || '—'}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </Modal>

        {/* Manual Payment Modal */}
        <Modal
          isOpen={manualPaymentModal.isOpen}
          onClose={handleCloseManualPaymentModal}
          title="Record Manual Payment"
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">
                Student: {manualPaymentModal.studentName}
              </p>
              <p className="text-sm text-gray-500">
                Program: {manualPaymentModal.programName}
              </p>
              <p className="text-sm text-gray-500">
                Total Invoice: ${manualPaymentModal.totalAmount.toFixed(2)}
              </p>
              <p className="text-sm text-gray-500">
                Already Paid: ${manualPaymentModal.amountPaid.toFixed(2)}
              </p>
              <p className="text-sm text-gray-600 font-medium">
                Remaining: ${manualPaymentModal.remainingAmount.toFixed(2)}
              </p>
            </div>
            <div>
              <label htmlFor="payment-amount" className="block text-sm font-medium text-gray-700">
                Payment Amount
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={manualPaymentModal.remainingAmount}
                  id="payment-amount"
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                  value={manualPaymentModal.paymentAmount}
                  onChange={(e) => setManualPaymentModal({ ...manualPaymentModal, paymentAmount: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <label htmlFor="payment-method" className="block text-sm font-medium text-gray-700">
                Payment Method
              </label>
              <select
                id="payment-method"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={manualPaymentModal.paymentMethod}
                onChange={(e) => setManualPaymentModal({ ...manualPaymentModal, paymentMethod: e.target.value })}
              >
                <option value="check">Check</option>
                <option value="cash">Cash</option>
                <option value="zelle">Zelle(Manual)</option>
              </select>
            </div>
            <div>
              <label htmlFor="payment-date" className="block text-sm font-medium text-gray-700">
                Payment Date
              </label>
              <input
                type="date"
                id="payment-date"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={manualPaymentModal.paymentDate}
                onChange={(e) => setManualPaymentModal({ ...manualPaymentModal, paymentDate: e.target.value })}
              />
            </div>
            {manualPaymentModal.error && (
              <div className="text-red-600 text-sm">{manualPaymentModal.error}</div>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCloseManualPaymentModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                disabled={manualPaymentModal.isProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleProcessManualPayment}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={manualPaymentModal.isProcessing}
              >
                {manualPaymentModal.isProcessing ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Invoice Details Modal */}
        <Modal
          isOpen={invoiceDetailsModal.isOpen}
          onClose={handleCloseInvoiceDetailsModal}
          title="Invoice Details"
        >
          {invoiceDetailsModal.invoice && (
            <div className="space-y-4">
              {invoiceDetailsModal.invoice.memo_code && (
                <div className="text-sm text-gray-500">
                  Memo Code: <span className="font-medium text-gray-800">{invoiceDetailsModal.invoice.memo_code}</span>
                </div>
              )}

              {invoiceDetailsModal.error ? (
                <p className="text-red-500">{invoiceDetailsModal.error}</p>
              ) : invoiceDetailsModal.isLoading || !invoiceDetailsModal.detail ? (
                <p className="text-sm text-gray-500">Loading invoice details...</p>
              ) : (
                <InvoiceDetailsBody
                  invoice={invoiceDetailsModal.detail}
                  lineItems={invoiceDetailsModal.lineItems}
                  isLineItemsLoading={false}
                />
              )}

              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  onClick={handleCloseInvoiceDetailsModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Edit Invoice Modal */}
        <Modal
          isOpen={editInvoiceModal.isOpen}
          onClose={handleCloseEditInvoiceModal}
          title="Edit Invoice"
        >
          {editInvoiceModal.invoice && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Student</p>
                  <p className="text-sm font-medium text-gray-800">{editInvoiceModal.invoice.student_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Program</p>
                  <p className="text-sm font-medium text-gray-800">{editInvoiceModal.invoice.program_name}</p>
                </div>
              </div>

              {/* Enrollment Details */}
              {editInvoiceModal.enrollmentDetails && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-3">📋 Enrollment Context</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-blue-700 font-medium">Start Date</p>
                      <p className="text-blue-800">{formatDateDisplay(editInvoiceModal.enrollmentDetails.enrollment_start_date)}</p>
                    </div>
                    <div>
                      <p className="text-blue-700 font-medium">Billing Type</p>
                      <p className="text-blue-800 capitalize">{editInvoiceModal.enrollmentDetails.program.billing_type.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-blue-700 font-medium">Program Type</p>
                      <p className="text-blue-800">{getProgramTypeDisplayName(editInvoiceModal.enrollmentDetails.program.type)}</p>
                    </div>
                    <div>
                      <p className="text-blue-700 font-medium">Base Price</p>
                      <p className="text-blue-800">${editInvoiceModal.enrollmentDetails.program.price.toFixed(2)}</p>
                    </div>
                    {editInvoiceModal.enrollmentDetails.selected_days && editInvoiceModal.enrollmentDetails.selected_days.length > 0 && (
                      <div className="col-span-2">
                        <p className="text-blue-700 font-medium">Selected Days</p>
                        <p className="text-blue-800">{editInvoiceModal.enrollmentDetails.selected_days.join(', ')}</p>
                      </div>
                    )}
                    {editInvoiceModal.enrollmentDetails.optInItemsDetails && editInvoiceModal.enrollmentDetails.optInItemsDetails.length > 0 && (
                      <div className="col-span-2">
                        <p className="text-blue-700 font-medium">Optional Items Included</p>
                        <div className="text-blue-800">
                          {editInvoiceModal.enrollmentDetails.optInItemsDetails.map((item, index) => (
                            <span key={item.id} className="inline-block mr-3">
                              {item.name} (-${item.amount.toFixed(2)})
                              {index < editInvoiceModal.enrollmentDetails!.optInItemsDetails!.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Invoice Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Status
                </label>
                <select
                  value={editInvoiceModal.editedStatus}
                  onChange={(e) => setEditInvoiceModal(prev => ({ 
                    ...prev, 
                    editedStatus: e.target.value 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                  <option value="voided">Voided</option>
                </select>
              </div>

              {/* Line Items */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Line Items</h4>
                {editInvoiceModal.isLoading ? (
                  <p className="text-sm text-gray-500">Loading line items...</p>
                ) : (
                  <div className="space-y-3">
                    {editInvoiceModal.editedLineItems.map((item, index) => {
                      // Find the original line item to get config details
                      const originalLineItem = editInvoiceModal.lineItems.find(li => li.id === item.id);
                      const isNewItem = item.id.startsWith('temp_');
                      const isOptional = isNewItem || originalLineItem?.is_optional === true;
                      
                      return (
                        <div key={item.id} className={`p-3 border rounded-md ${isNewItem ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                          {/* Line Item Config Info (Read-only) */}
                          {originalLineItem?.line_item_configs && (
                            <div className="mb-2 text-xs text-gray-600">
                              <span className="font-medium">{originalLineItem.line_item_configs.name}</span>
                              {originalLineItem.line_item_configs.type && (
                                <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-gray-700 capitalize">
                                  {originalLineItem.line_item_configs.type.replace('_', ' ')}
                                </span>
                              )}
                              {!isNewItem && (
                                <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                  originalLineItem?.is_optional 
                                    ? 'bg-blue-100 text-blue-700' 
                                    : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {originalLineItem?.is_optional ? 'Optional' : 'Required'}
                                </span>
                              )}
                            </div>
                          )}
                          {isNewItem && (
                            <div className="mb-2 text-xs text-green-700">
                              <span className="font-medium">🆕 New Item</span>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Description</label>
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => {
                                  const newItems = [...editInvoiceModal.editedLineItems];
                                  newItems[index] = { 
                                    ...newItems[index], 
                                    description: e.target.value,
                                    isEdited: true 
                                  };
                                  setEditInvoiceModal(prev => ({ 
                                    ...prev, 
                                    editedLineItems: newItems 
                                  }));
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <label className="block text-xs text-gray-500 mb-1">Amount</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={item.amount}
                                  onChange={(e) => {
                                    const newItems = [...editInvoiceModal.editedLineItems];
                                    newItems[index] = { 
                                      ...newItems[index], 
                                      amount: parseFloat(e.target.value) || 0,
                                      isEdited: true 
                                    };
                                    setEditInvoiceModal(prev => ({ 
                                      ...prev, 
                                      editedLineItems: newItems 
                                    }));
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                              {(isOptional || canFreelyEditInvoice) && (
                                <div className="flex items-end">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newItems = editInvoiceModal.editedLineItems.filter((_, i) => i !== index);
                                      setEditInvoiceModal(prev => ({
                                        ...prev,
                                        editedLineItems: newItems
                                      }));
                                    }}
                                    className="px-2 py-1 text-xs text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
                                    title={
                                      isNewItem
                                        ? "Remove this new item"
                                        : isOptional
                                          ? "Remove this optional line item from invoice"
                                          : "Remove this line item (allowed because the invoice email is suppressed)"
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add Optional Items */}
                {editInvoiceModal.enrollmentDetails?.availableOptionalItems && 
                 editInvoiceModal.enrollmentDetails.availableOptionalItems.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Add Optional Items</h5>
                    <p className="text-xs text-gray-500 mb-3">
                      These items were available but not included in the original enrollment
                    </p>
                    <div className="space-y-2">
                      {editInvoiceModal.enrollmentDetails.availableOptionalItems
                        .filter(optItem => {
                          // Only show items that aren't already in the invoice (including current edits)
                          const existingIds = editInvoiceModal.lineItems.map(li => li.line_item_configs?.id).filter(Boolean);
                          const currentEditedIds = editInvoiceModal.editedLineItems.map(ei => {
                            // For new items, find the matching optional item ID
                            if (ei.id.startsWith('temp_')) {
                              const matchingOpt = editInvoiceModal.enrollmentDetails?.availableOptionalItems?.find(
                                opt => opt.name === ei.description || opt.description === ei.description
                              );
                              return matchingOpt?.id;
                            }
                            // For existing items, find their line_item_config_id
                            const originalItem = editInvoiceModal.lineItems.find(li => li.id === ei.id);
                            return originalItem?.line_item_configs?.id;
                          }).filter(Boolean);
                          
                          return !existingIds.includes(optItem.id) && !currentEditedIds.includes(optItem.id);
                        })
                        .map(optItem => (
                          <div key={optItem.id} className="flex justify-between items-center p-2 bg-white border border-gray-200 rounded text-sm">
                            <div className="flex-1">
                              <div className="font-medium">{optItem.name}</div>
                              <div className="text-xs text-gray-500">
                                <span className="capitalize">{optItem.type ? optItem.type.replace('_', ' ') : 'Unknown'}</span>
                                {optItem.description && ` • ${optItem.description}`}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-gray-700">${optItem.amount.toFixed(2)}</span>
                              <button
                                type="button"
                                onClick={() => handleAddOptionalLineItem(optItem)}
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}

                {/* Add Custom Line Item — only for email-suppressed invoices */}
                {canFreelyEditInvoice && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <h5 className="text-sm font-medium text-amber-900 mb-2">Add Custom Line Item</h5>
                    <p className="text-xs text-amber-700 mb-3">
                      This invoice's email is suppressed, so you can append any line item and
                      customize its description and amount before sending.
                    </p>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Line item config</label>
                        <select
                          value={customLineItemConfigId}
                          onChange={(e) => setCustomLineItemConfigId(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select a line item…</option>
                          {(editInvoiceModal.allLineItemConfigs ?? [])
                            .filter(cfg => !presentLineItemConfigIds.has(cfg.id))
                            .map(cfg => (
                              <option key={cfg.id} value={cfg.id}>
                                {cfg.name} ({cfg.type.replace('_', ' ')})
                              </option>
                            ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        disabled={!customLineItemConfigId}
                        onClick={() => {
                          const cfg = (editInvoiceModal.allLineItemConfigs ?? []).find(c => c.id === customLineItemConfigId);
                          if (cfg) {
                            handleAddCustomLineItem(cfg);
                            setCustomLineItemConfigId('');
                          }
                        }}
                        className="px-3 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Calculated Total */}
              <div className="border-t border-gray-200 pt-4">
                <div className="text-right space-y-1">
                  <p className="text-sm text-gray-500">
                    New Total: 
                    <span className="font-medium text-gray-800 ml-1">
                      ${editInvoiceModal.editedLineItems.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Note: Invoice total will be automatically updated when line items are saved
                  </p>
                </div>
              </div>

              {editInvoiceModal.error && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                  {editInvoiceModal.error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseEditInvoiceModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  disabled={editInvoiceModal.isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveInvoiceEdit}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                  disabled={editInvoiceModal.isSaving}
                >
                  {editInvoiceModal.isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </AdminLayout>
  );
} 